import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  GamePresentationContext,
  GameSceneContext,
  MissionDefinition,
  MissionStageDefinition,
  ParentGameSummary,
  RewardResult,
  ThriveGameModule,
} from '../../../sdk';
import type { InputCapability } from '../../../shared/types/capabilities';
import type { PlayerId } from '../../../shared/types/ids';
import { playerLabel, type PlayerRuntimeState } from '../../../shared/types/players';
import type { CameraMode, JumpSubstitute } from '../../../shared/types/camera-mode';
import type { CompletedGameSession, SessionInputMode } from '../../../shared/types/session';
import type { SkillEstimate } from '../../../shared/types/skills';
import type { SessionLaunchConfig } from '../../../shared/types/session-launch';
import { createSemanticEventBus, createSemanticInputReader } from '../../../interaction/events';
import {
  createKeyboardInputAdapter,
  createKeyboardSessionControls,
  HERO_1_PLAYER_ID,
  HERO_2_PLAYER_ID,
  keyboardBindingsFor,
  type KeyboardInputContext,
} from '../../../interaction/keyboard';
import {
  createCameraInputAdapter,
  createCooperativeCameraAdapter,
  createCooperativeTrackingRecoveryMonitor,
  DEFAULT_POSE_INPUT_CONTEXT,
  type CameraInputAdapter,
  type CooperativeCameraAdapter,
  type CooperativeLaneRecovery,
  type CooperativeLossReason,
  type CooperativeTrackingRecoveryMonitor,
  type PoseInputContext,
} from '../../../interaction/camera';
import { requiredCameraCapabilities } from '../../../interaction/camera/camera-mode';
import {
  createTrackingRecoveryMonitor,
  type TrackingRecoveryMonitor,
  type TrackingRecoveryState,
} from '../../../interaction/camera/tracking-recovery';
import { hasTrackingQualityFallen, poseCalibrationFrom } from '../../../interaction/calibration';
import type { LaneResolver } from '../../../interaction/pose';
import type { CalibrationProfile, CalibrationReuseRefusal } from '../../../interaction/calibration';
import { INVENTED_CAMERA_ACTIVITY_COPY } from '../../../platform/config/camera-activity';
import {
  CAMERA_MODE_COPY,
  INVENTED_CAMERA_OVERRIDE_COPY,
  INVENTED_CAMERA_STAGE_COPY,
  JUMP_SUBSTITUTE_COPY,
  JUMP_SUBSTITUTES,
} from '../../../platform/config/camera-mode';
import {
  INVENTED_RECOVERY_COPY,
  laneInvitationFor,
  recoveryInvitationFor,
} from '../../../platform/config/camera-recovery';
import { canResumeMissionProgress, createMissionRuntime } from '../../../platform/missions';
import type { MissionRuntimeEvent } from '../../../platform/missions';
import { observeNarration, type NarrationActivity } from '../../../platform/narration';
import type { AudioState } from '../../../platform/audio';
import { createStageAudio } from './stage-audio';
import { learningSubjectIdOf } from '../../../platform/skills/learning-subject';
import { createThemeAssetResolver } from '../../../platform/theme';
import {
  createThriveAppEventBus,
  type AssetResolutionReport,
  type ParentOverrideCommand,
  type ThriveAppEventBus,
} from '../../../platform/runtime/app-events';
import { createMissionEventRelay } from '../../../platform/runtime/mission-event-relay';
import {
  createStageSceneDirector,
  type StageSceneDirector,
} from '../../../platform/runtime/stage-scene-director';
import type { MissionProgressSnapshot } from '../../../shared/types/mission-progress';
import { currentErasureEpoch, scopeRepositoriesToSitting } from '../../../storage/erasure-boundary';
import type { ThriveRepositories } from '../../../storage/interfaces/repositories';
import { BUILT_IN_LAUNCH_DEFAULTS } from '../../session/launch-defaults';
import { resolveSitting } from '../../session/resolve-sitting';
import { buildSessionPlayers } from '../../session/session-players';
import { useAppServices, type ActivePlayer } from '../../shell/services-context';
import { ParentSummaryView } from '../../summary/ParentSummaryView';
import {
  isAttemptWindow,
  cameraQualityCoverage,
  coverageFromHistory,
  withCameraBodySeen,
  withCameraSeats,
  NO_CAMERA_COVERAGE,
  type CameraCoverageHistory,
  type SittingCameraQuality,
  disruptiveInterruptions,
  interruptionsBetween,
  NO_CAMERA_ACTIVITY,
  observeCameraActions,
  recognisedShare,
  sittingCameraQuality,
  withFallbackOffer,
  withLossEpisode,
  withRecognisedAction,
  withUncertaintyCleared,
  withUncertaintyEpisode,
  type CameraActivityTally,
  type CameraActivityContext,
} from './camera-activity';
import { pauseTransition, withPauseCause, type StagePauseCause } from './pause-causes';
import { selectStageAssetIds } from './stage-assets';
import { StageArt } from './StageArt';
import { PLATFORM_ASSET_ROLES, stageRecoveryArtFor } from '../../../platform/config/stage-art';
import { StageCalibrationOverlay } from './StageCalibrationOverlay';

/**
 * Hosts the Phaser stage inside React.
 *
 * React and Phaser only ever speak through the typed bridge (architecture §6) — the pause and
 * exit buttons below send requests and wait for the stage to answer, rather than reaching into
 * the game instance. Input flows keyboard → semantic bus → scene, and the readout under the
 * canvas mirrors what the scene received, so the whole chain is visible.
 */

/**
 * What a grown-up can do mid-mission, and what each control says (game design spec §32).
 *
 * Phase 1 offers exactly the three a keyboard sitting can honour. Mark correct, retry detection,
 * restart calibration and change-jump-substitute are camera-era controls (Phase 3) and are not
 * shown as dead buttons on a parent's screen.
 *
 * The register is the parent's, and the vocabulary rule is the summary's: nothing here names a
 * mistake, a failure or a comparison, because none of these actions is one. Skipping a round
 * records a non-response, never a wrong answer.
 */
const PARENT_ACTIONS: readonly {
  testId: string;
  command: ParentOverrideCommand['type'];
  label: string;
  description: string;
}[] = [
  {
    testId: 'parent-action-skip',
    command: 'SKIP_ROUND',
    label: 'Skip this round',
    description: 'Move on to the next question. Nothing is marked wrong.',
  },
  {
    testId: 'parent-action-simplify',
    command: 'SIMPLIFY',
    label: 'Make it simpler',
    description: 'Offer an easier version of the same question.',
  },
  {
    testId: 'parent-action-end',
    command: 'END_SESSION',
    label: 'End session safely',
    description: 'Stop here and see how the sitting went. The shards collected are kept.',
  },
];

/**
 * What applying a fresh calibration did (Slice G).
 *
 * Reported rather than assumed: a second run of hero training can still fail architecture §20's
 * reuse rules — a child who could not manage the move this mission needs is exactly the case
 * D-066(5) is about — and a family that is never told cannot do anything about it.
 */
interface CameraOverrideOutcome {
  readonly attached: boolean;
  readonly refusals: readonly CalibrationReuseRefusal[];
}

/** The camera-era half of GDS §32's register, as the boot effect can honour it. */
interface CameraOverrides {
  /**
   * Applies fresh measurements. In a cooperative sitting the hero is named and **only** their lane
   * changes (Slice F′, D-076(8)); a single-player sitting passes nothing and behaves as it always
   * has.
   */
  applyCalibration(playerId?: string): CameraOverrideOutcome;
  setJumpSubstitute(substitute: JumpSubstitute): void;
}

/** What a keyboard-only session can actually produce today (D-006). */
const KEYBOARD_CAPABILITIES: InputCapability[] = [
  'PLAYER_PRESENCE',
  'BODY_ZONE',
  'JUMP',
  'HANDS_RAISED',
];

/** If the stage never acknowledges an exit, leave anyway rather than trapping the player. */
const EXIT_FALLBACK_MS = 1_500;

/**
 * Why a pause was asked for, as it travels over the React↔Phaser bridge.
 *
 * The reasons are load-bearing in one place only: a pause the stage itself raised must not be fed
 * back into its own cause tracking (see `shell:pause-requested` below). Everything else about them
 * is diagnostic.
 */
const PARENT_PAUSE_REASON = 'parent-control';
const CAMERA_PAUSE_REASON = 'camera-tracking-lost';
const STAGE_PAUSE_REASONS: ReadonlySet<string> = new Set([
  PARENT_PAUSE_REASON,
  CAMERA_PAUSE_REASON,
]);

/** Nothing is wrong with the camera. The state a keyboard sitting stays in for ever. */
const CAMERA_HEALTHY: TrackingRecoveryState = {
  trackingLost: false,
  lowPerformance: false,
  fallbackOffered: false,
};

export interface GameStageProps {
  gameModule: ThriveGameModule;
}

/**
 * Which meaning the S key carries for this mechanic (D-006, D-035).
 *
 * A keypress carries no mechanic, so the runtime has to say. It is only ever set *between*
 * rounds: switching mid-round releases held state and clears the latched zone, which would
 * retract a posture a child is still holding as their answer.
 */
function inputContextFor(inputMethod: string): KeyboardInputContext {
  return inputMethod === 'hands-or-crouch' ? 'BINARY' : 'ZONES';
}

/**
 * How a key code reads on screen.
 *
 * The help is generated from the adapter's own bindings rather than written out beside them, so
 * a remapped key cannot leave a family reading instructions for keys that no longer do anything.
 */
const KEY_LABELS: Readonly<Record<string, string>> = {
  KeyA: 'A',
  KeyS: 'S',
  KeyD: 'D',
  KeyW: 'W',
  Space: 'Space',
  ArrowLeft: 'Left arrow',
  ArrowDown: 'Down arrow',
  ArrowRight: 'Right arrow',
  ArrowUp: 'Up arrow',
  Enter: 'Enter',
};

/**
 * One hero's keys, described for a grown-up looking over a shoulder.
 *
 * Named by lane rather than by child, and identical in wording for both heroes: the two key sets
 * are two ways to give the same answer, not two roles (non-competitive invariant, COOP-08).
 */
function HeroKeyHelp({ playerId, name }: { playerId: PlayerId; name: string | null }) {
  const bindings = keyboardBindingsFor(playerId);
  const key = (code: string) => <kbd>{KEY_LABELS[code] ?? code}</kbd>;

  return (
    <>
      {name === null ? null : <strong>{name}: </strong>}
      Press{' '}
      {Object.keys(bindings.zones).map((code) => (
        <span key={code}>{key(code)} </span>
      ))}
      to change zone, {key(bindings.hands)} to raise hands, {key(bindings.action)} to jump or count.
      In a yes/no round {key(bindings.centreOrCrouch)} is a crouch.{' '}
    </>
  );
}

/** Round lifecycle → the typed bridge the scene draws from. React never reaches into Phaser. */
function publishRuntimeEvent(bridge: ThriveAppEventBus, event: MissionRuntimeEvent): void {
  switch (event.type) {
    case 'ROUND_PRESENTED':
      bridge.emit('mission:round-presented', {
        roundIndex: event.roundIndex,
        stageId: event.stageId,
        contentId: event.contentId,
        attemptNumber: event.attemptNumber,
        simplified: event.simplified,
        promptText: event.prompt.text,
        choiceLabels: (event.prompt.choices ?? []).map((choice) => choice.label),
      });
      break;
    case 'ROUND_RESOLVED':
      bridge.emit('mission:round-resolved', {
        roundIndex: event.roundIndex,
        contentId: event.contentId,
        completed: event.completed,
        shardsAwarded: event.shardsAwarded,
      });
      break;
    case 'ROUND_ASSIST':
      bridge.emit('mission:assist', {
        roundIndex: event.roundIndex,
        assist: event.assist,
        ...(event.text === undefined ? {} : { text: event.text }),
      });
      break;
    case 'SCORE_CHANGED':
      bridge.emit('mission:score-changed', {
        label: event.score.label,
        value: event.score.value,
        progress: event.score.progress,
      });
      break;
    case 'MISSION_COMPLETED':
      bridge.emit('game:completed', { summary: event.session.summary });
      break;
    default:
      break;
  }
}

export interface RoundReadout {
  stageId: string;
  contentId: string;
  promptText: string;
  attemptNumber: number;
  simplified: boolean;
  choiceLabels: string[];
}

/** What the runtime handed over when the mission run ended. */
interface MissionCompletion {
  session: CompletedGameSession;
  reward: RewardResult;
  /** A grown-up ended the sitting; the mission itself did not finish (game design spec §32). */
  endedEarly: boolean;
}

export function GameStage({ gameModule }: GameStageProps) {
  const {
    activePlayer,
    tunables,
    narration,
    repositories,
    sessionLaunchConfig,
    setSessionLaunchConfig,
    camera,
    cameraInputEnabled,
    audio,
  } = useAppServices();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement | null>(null);
  /** The mirrored feed the stage is drawn over, in a camera sitting (MVP notes §16). */
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The config this run actually used — the chosen one, or the defaults the stage computed when
   * it was launched without one. "Play again" reuses it, so a replay keeps the parent's choices.
   */
  const launchConfigRef = useRef<SessionLaunchConfig>(BUILT_IN_LAUNCH_DEFAULTS);
  /**
   * Who this run resolved as, latched for the life of the stage (D-054(2), Slice R).
   *
   * A sitting restored after a reload has no `activePlayer` in shell state — nothing writes one
   * back, because doing so would re-run this boot effect mid-mission. Without the latch, "Play
   * again" would reboot with nobody named and silently demote a named child to a guest, writing
   * the replay's evidence nowhere.
   */
  const resolvedPlayerRef = useRef<ActivePlayer | null>(null);
  /**
   * Everything holding this mission still, right now (CAM-05).
   *
   * A ref beside the state because the decision has to be synchronous: a frame that recovers the
   * child must resume the runtime *before* the next detector output on that same frame becomes an
   * answer, and React state is not available until the next render.
   */
  const pauseCausesRef = useRef<readonly StagePauseCause[]>([]);
  /** Ends the camera half of the sitting, set once the stage knows there is one. */
  const releaseCameraRef = useRef<(() => void) | null>(null);
  /**
   * The camera-era parent overrides, as the boot effect wired them (GDS §32, Slice G).
   *
   * A ref for the same reason `releaseCameraRef` is one: the controls that call these live on the
   * pause overlay, outside the effect that owns the input chain, and neither of them is a thing the
   * mission runtime should ever hear about.
   */
  const cameraOverridesRef = useRef<CameraOverrides | null>(null);
  /**
   * "The mission has stopped listening" — reaching the camera adapter (S1-3, D-068(2)).
   *
   * A pause is a moment in which nothing a child does can be an answer, and on the keyboard that is
   * free: the `keydown` of an action begun behind the overlay is consumed while the runtime is
   * ignoring input, and the `keyup` says nothing. A body has no keyup, so a jump that was in the air
   * when the grown-up pressed Pause would otherwise land after Resume and score. This discards the
   * movement in progress — silently, and without touching a held posture, which is exactly what the
   * keyboard's own latches do across a pause.
   */
  const neutralizeCameraRef = useRef<(() => void) | null>(null);
  /**
   * "Can this profile still serve this sitting?", asked again once the mission's stream is open
   * (S1-7, architecture §20).
   *
   * The launch answer is necessarily provisional: browsers withhold the device id until permission
   * is granted, and the stream opens after the boot effect has already resolved. Asking once more
   * when the camera is actually live is what makes the `CAMERA_CHANGED` rule reachable at all —
   * calibrate on the laptop's camera, unplug it, launch on the external one, and a five-year-old
   * would otherwise be read against another lens's measurements. Returns the refusals, if any.
   */
  const revalidateCameraRef = useRef<(() => readonly CalibrationReuseRefusal[]) | null>(null);
  /**
   * The same question, asked per lane in a cooperative sitting (Slice F′, D-081(7) retired).
   *
   * Returns one entry per **live camera lane** whose profile can no longer serve this mission.
   * Judgment per lane is deliberate and permitted: D-076(6) forbids per-child *surfaces* and
   * comparisons, not the internal arithmetic that keeps a child's evidence honest. Nothing this
   * returns is ever rendered per child — the refusal notice it feeds is the shipped sitting-wide
   * one, which names codes and no children.
   */
  const revalidateLanesRef = useRef<
    (() => readonly { playerId: string; refusals: readonly CalibrationReuseRefusal[] }[]) | null
  >(null);
  /**
   * One hero stops answering with their body, and only that hero (Slice F′, D-076(5)).
   *
   * Returns the heroes still on the camera afterwards, so the caller can tell the difference
   * between "one lane went" and "the sitting has no camera left". Held in a ref because every
   * caller — the per-lane offer on the stage, the recalibration triggers — lives outside the effect
   * that owns the input chain.
   */
  const releaseLaneRef = useRef<((playerId: PlayerId) => readonly string[]) | null>(null);

  const bridge = useMemo(() => createThriveAppEventBus(), []);

  const [sceneReady, setSceneReady] = useState(false);
  const [paused, setPaused] = useState(false);
  /** The same list as `pauseCausesRef`, for rendering. */
  const [pauseCauses, setPauseCauses] = useState<readonly StagePauseCause[]>([]);
  /** What the camera watcher believes. Never anything but healthy in a keyboard sitting. */
  const [recovery, setRecovery] = useState<TrackingRecoveryState>(CAMERA_HEALTHY);
  /**
   * Which of GDS §36's situations is holding the mission, so the child reads the right invitation.
   *
   * The come-back overlay is the wrong thing to show a child who never left, and the wrong thing
   * to show one whose camera stopped while they stood exactly where they should be.
   */
  const [recoveryReason, setRecoveryReason] = useState<CooperativeLossReason | null>(null);
  /**
   * Which heroes the camera cannot read right now, in a cooperative sitting (Slice E′).
   *
   * Beside `recovery` rather than inside it, because the two say different things and only one of
   * them holds the mission: this list is the **soft** state D-076(2)(a) requires — one child's
   * trouble is theirs alone, and the sibling must not be slowed by a millisecond for it. When every
   * lane is on this list the sitting is held, and that is `recovery.trackingLost` above.
   */
  const [laneRecovery, setLaneRecovery] = useState<readonly CooperativeLaneRecovery[]>([]);
  /**
   * What to call each hero on their own invitation, from the roster the stage actually built.
   *
   * A panel addressed to somebody rather than about them, which is the whole difference between
   * naming a seat and comparing two children.
   */
  const [heroLabels, setHeroLabels] = useState<Readonly<Record<string, string>>>({});
  /**
   * Who a mid-mission hero training would measure, and for which play space (Slice G).
   *
   * Non-null in every sitting a grown-up asked the camera for — including one architecture §20
   * refused a profile for, which is exactly the sitting D-066(5) says this override recovers.
   */
  const [cameraSetup, setCameraSetup] = useState<{ playerId: string; mode: CameraMode } | null>(
    null,
  );
  /**
   * Which heroes are answering with their **bodies** right now (Slice F′, D-076(5)).
   *
   * The per-child entry guard's answer, and the one thing several surfaces need: the grown-up
   * diagnostic on the readout, the chooser on the pause panel, and the decision about whether the
   * sitting still has a camera at all. It shrinks when a lane is released and never grows — a
   * mid-sitting keys→camera upgrade is deliberately not built (see the boot effect).
   */
  const [cameraLaneHeroes, setCameraLaneHeroes] = useState<readonly string[]>([]);
  /**
   * Which hero the grown-up picked for a mid-mission retraining (D-076(8), retiring D-081(8)).
   *
   * Null means "whichever the chooser is showing", which is the first camera hero — so a
   * single-player sitting, whose chooser does not exist, behaves exactly as it did before.
   */
  const [retrainingPlayerId, setRetrainingPlayerId] = useState<string | null>(null);
  /**
   * Heroes who took the per-child keyboard offer (D-076(5)).
   *
   * Kept beside `cameraLaneHeroes` rather than derived from it because the two say different
   * things: a hero can be off the camera because they never trained (nothing to say) or because
   * they *chose* the keys (which is worth telling them, once).
   */
  const [keyboardElectedHeroes, setKeyboardElectedHeroes] = useState<readonly string[]>([]);
  /** Hero training is on screen over the paused mission (GDS §32 "Restart calibration"). */
  const [calibrating, setCalibrating] = useState(false);
  /**
   * Two heroes are answering with their bodies (Slice D′).
   *
   * Held in state because two shipped single-player surfaces are deliberately **not** extended to a
   * cooperative sitting by this slice, and both of them live outside the boot effect: the camera-era
   * parent overrides (per-child scoping is D-076(8)/Slice F′) and the mid-mission recalibration
   * triggers (whose two halves are a per-child quality question D-076(6) forbids answering per child
   * and a fallback-scope question F′ owns). Their absence is pinned by test so it reads as the
   * decision it is rather than as an oversight.
   */
  const [cooperativeCameraSitting, setCooperativeCameraSitting] = useState(false);
  /**
   * A fresh set of measurements is waiting for the grown-up's Resume (D-059 PS3-2).
   *
   * Deliberately *pending* rather than applied: the numbers every detector reads must never change
   * under a child who is mid-round, so they take effect at the same moment the mission does.
   */
  const [pendingCalibration, setPendingCalibration] = useState<{
    readonly playerId: string | null;
  } | null>(null);
  /**
   * Whose training is on screen right now, fixed when it opened (S6).
   *
   * The chooser asks *who to train*, and it asks before the training. Once a child has stood
   * through seven steps the measurements are theirs, and a later touch of the chooser must not hand
   * them to their sibling — nor restart the finished training as the other child's, which is what
   * re-reading the chooser at Resume did. `null` in a single-player sitting, which has one hero and
   * therefore nothing to name.
   */
  const [trainingHeroId, setTrainingHeroId] = useState<string | null>(null);
  /** What a fresh training could not fix, when it could not. */
  const [calibrationNote, setCalibrationNote] = useState<string | null>(null);
  /** A grown-up took GDS §36's last rung and turned the camera off for this sitting. */
  const [cameraFallbackElected, setCameraFallbackElected] = useState(false);
  /**
   * What the camera managed this sitting (CAM-08).
   *
   * A ref beside the state for the usual reason: the counts are raised from inside the input chain,
   * where several can land between two renders, and each one has to be added to the count as it
   * stood a microsecond ago rather than to whatever React last rendered. Session-only in the
   * strongest sense — it is not persisted, not carried into a replay, and not in any snapshot.
   */
  const cameraActivityRef = useRef<CameraActivityTally>(NO_CAMERA_ACTIVITY);
  const [cameraActivity, setCameraActivity] = useState<CameraActivityTally>(NO_CAMERA_ACTIVITY);
  /**
   * What the camera covered over the whole sitting, not at the end of it (S7).
   *
   * Beside the tally because it is the same kind of fact: cumulative, session-only, never persisted,
   * and about the sitting rather than about a child. Two monotonic booleans, folded from who was on
   * the camera at each moment — see `withCameraSeats`.
   */
  const cameraCoverageRef = useRef<CameraCoverageHistory>(NO_CAMERA_COVERAGE);
  /** How many heroes are seated in this sitting, for the coverage fold above. */
  const seatedHeroCountRef = useRef(0);
  /**
   * What the camera covered, frozen when the parent summary is built (S7).
   *
   * A snapshot rather than a live derivation, because the summary describes a sitting that has
   * finished: it is taken at the moment the runtime hands over its session, from the same refs the
   * CAM-08 counts live in, and nothing that happens afterwards can move it.
   */
  const [summaryCameraQuality, setSummaryCameraQuality] = useState<SittingCameraQuality | null>(
    null,
  );

  /**
   * How a mid-mission hero training asks the sitting whose body it is looking at (S3, D-076(1)).
   *
   * The cooperative pair's own lane view, published as state when the pair is wired and withdrawn
   * with it. `null` means there is nothing entitled to say whose body is in the picture, and the
   * honest answer to that is to offer no training at all rather than run one that guesses — the
   * trainer never builds an identity owner of its own, because a sitting has exactly one.
   */
  const [retrainingLaneView, setRetrainingLaneView] = useState<(() => LaneResolver) | null>(null);

  /**
   * Who is answering with their body, recorded once for every surface that asks (S4/S7 family).
   *
   * Every change of the camera's seats goes through here, because two facts are kept: the state the
   * surfaces render, and the sitting's cumulative coverage history — and a history that misses a
   * state it was never told about is exactly the finding this closes. The fold is monotonic and
   * idempotent, so calling it twice with the same seats costs nothing; `restart` is the new sitting
   * a "play again" builds, which starts the history again *with these seats* rather than empty.
   */
  const noteCameraLaneHeroes = useCallback((heroes: readonly string[], restart = false): void => {
    cameraCoverageRef.current = withCameraSeats(
      restart ? NO_CAMERA_COVERAGE : cameraCoverageRef.current,
      seatedHeroCountRef.current,
      heroes.length,
    );
    // Idempotent in the React sense as well as the fold's: every camera attach and detach routes
    // through here (S2-6), including the ones that re-state seats the sitting already has, and a
    // fresh array identity for an unchanged set would re-subscribe the watches keyed on it.
    setCameraLaneHeroes((current) =>
      current.length === heroes.length && current.every((id, index) => id === heroes[index])
        ? current
        : heroes,
    );
  }, []);

  /**
   * The camera read a body — the one thing that makes this sitting worth reporting on (S2-5).
   *
   * Kept apart from the seats above because they are different facts and the summary needs both:
   * seats decide *what the figures cover*, and this decides *whether there are figures at all*. A
   * sitting whose children never appeared has a wired camera and nothing to say about one.
   */
  const noteCameraBodySeen = useCallback((): void => {
    cameraCoverageRef.current = withCameraBodySeen(cameraCoverageRef.current);
  }, []);

  const [feedLines, setFeedLines] = useState<string[]>([]);
  const [assetReports, setAssetReports] = useState<AssetResolutionReport[]>([]);
  const [stageError, setStageError] = useState<string | null>(null);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [round, setRound] = useState<RoundReadout | null>(null);
  const [assist, setAssist] = useState<string | null>(null);
  /**
   * What the mission has asked to be said, so the repeat control is observable (NAR-01/02).
   * Speech itself leaves no trace — off, muted, or voiceless devices are all silent — so the
   * request is what the readout reports.
   */
  const [narrationActivity, setNarrationActivity] = useState<NarrationActivity>({
    utterances: 0,
    lastText: null,
    history: [],
  });
  /**
   * What the speakers are doing, for a grown-up and for the end-to-end suite.
   *
   * Sound leaves no trace an automated browser can see — a headless Chromium renders to a null
   * sink — so the only honest way to assert "the bed started and dipped while Bip-Bop spoke" is at
   * the seam where those decisions are made. Exactly the argument `observeNarration` settled for
   * speech (S5-8), and the same shape: a readout, never a control.
   */
  const [audioState, setAudioState] = useState<AudioState | null>(null);
  /** Cumulative: see the subscription below (R2-12). */
  const [audioHeard, setAudioHeard] = useState<string[]>([]);
  const [shards, setShards] = useState(0);
  /**
   * Whether a second hero is seated, which is the only thing the on-screen help needs to know.
   * Read from the roster the stage actually built, not from the launch config: a deep link or a
   * refresh arrives without one, and showing keys nobody is holding is worse than showing none.
   */
  const [coopKeyHelp, setCoopKeyHelp] = useState(false);
  /**
   * How this sitting is answering, as the stage actually wired it.
   *
   * `MIXED` rather than `CAMERA` on purpose, and it is the honest word: the keyboard adapter stays
   * attached and fully live in a camera sitting (CAM-09), so either input could have produced any
   * answer in the session being recorded. Claiming `CAMERA` would tell a parent something the
   * platform did not observe.
   */
  const [inputMode, setInputMode] = useState<SessionInputMode>('KEYBOARD');
  /**
   * Why architecture §20 refused to reuse this sitting's calibration, if it did (D-064(5)).
   *
   * Empty in every sitting that is playing normally, and in every keyboard sitting — a family that
   * never asked for the camera is not owed an explanation about it.
   */
  const [cameraRefusals, setCameraRefusals] = useState<readonly CalibrationReuseRefusal[]>([]);
  /** The play space and counting move this run wired up, for the grown-up-facing diagnostics. */
  const [playSpace, setPlaySpace] = useState<{
    mode: CameraMode;
    substitute: JumpSubstitute | null;
  } | null>(null);
  /**
   * The mission run is over — either it finished or a grown-up ended the sitting. Kept apart from
   * `completion.endedEarly` so nothing on screen can call the second one a finished mission.
   */
  const [missionEnded, setMissionEnded] = useState(false);
  /** The same fact as `missionEnded`, readable synchronously from inside the input chain (S1-8). */
  const missionEndedRef = useRef(false);
  /**
   * Whether a question is on screen waiting for an answer (S2-6).
   *
   * Not the same as "the mission is not paused", which is what CAM-08 used to measure: a mission
   * that is running is very often not *asking* anything — the opening narrative beat, the moment
   * after an answer while the feedback plays, the gap between one round resolving and the next being
   * presented. A ref because the input chain reads it synchronously, between two renders.
   */
  const roundOpenRef = useRef(false);
  const [completion, setCompletion] = useState<MissionCompletion | null>(null);
  /** How many times a grown-up used a mid-mission override this sitting (PS6-9, aggregate only). */
  const parentOverridesRef = useRef(0);
  const [parentOverridesUsed, setParentOverridesUsed] = useState<number | null>(null);
  /**
   * The most recent snapshot body this stage has seen, and the only body it will ever write itself
   * (P6R12-1).
   *
   * Recorded **synchronously, as the runtime hands it over** — before the repository's write queue,
   * and therefore before anything can interleave. That is what makes the durable write below safe
   * without a read-modify-write: at the instant a grown-up presses something, this holds the newest
   * body that exists on this side of the seam, so the write cannot resurrect an older round position.
   * Cleared with the record it describes, so a mission that has been released cannot be written back.
   */
  const latestSnapshotRef = useRef<MissionProgressSnapshot | null>(null);
  /**
   * The erasure epoch this sitting was born under (P6R13-1, BL-003).
   *
   * A parent's delete can happen while this stage is on screen — in the grown-up's own tab, or in
   * this one — and when it does, everything this stage is holding describes data that no longer
   * exists. `storage/erasure-boundary.ts` explains why that is modelled as an epoch rather than as
   * an event; what it means here is that a sitting older than the current epoch has lost the right
   * to write, and finds out by asking rather than by being lucky.
   */
  /**
   * The erasure epoch **this run** was born under (P6R14-4).
   *
   * A sitting is not a component. "Play again" starts a genuinely new run without remounting
   * anything, and round 13's component-lifetime capture left that new run inheriting the old one's
   * staleness — permanently refusing every checkpoint while storage reported itself perfectly
   * healthy, which is the worst shape a persistence bug can take. The boot effect, which is what
   * starts a run, refreshes this for each one.
   */
  const sittingEpochRef = useRef(currentErasureEpoch());
  /**
   * Which **generation** of each named child this run is for (P6R16-2, D-056(6)).
   *
   * Profile ids are caller-supplied and reusable, so an id alone cannot tell a child from a child
   * created under the same id after the first was deleted — which is exactly what a family who
   * deletes everything and types the same name again produces. `createdAt` is the generation, it is
   * already stored, and the boot effect below already reads it for the launch-identity record; this
   * keeps it so the storage layer can refuse a write that would attach one child's learning to
   * another.
   */
  const sittingGenerationsRef = useRef<ReadonlyMap<string, string>>(new Map());
  /**
   * The same one count, projected onto the sitting's own snapshot (P6R11-1).
   *
   * A mount is not a sitting: a five-year-old's tablet gets refreshed, PRO-03 restores the session
   * id, the round, the score and the spent teaching moment around it — and the override tally was
   * the one fact that did not come back, so a resumed evening's completed record reported only the
   * help given after the reload. That is an observation instrument under-reporting a family's
   * evening, which is what PS6-9 exists to avoid.
   *
   * Written **here, at the boundary**, rather than inside the mission runtime: the runtime is frozen
   * and has no business knowing what a grown-up pressed on the stage chrome, and adding a count
   * there would be a second tally that could disagree with this one. `noteParentOverride` remains
   * the single counting site (D-109's one-instrument rule); this is a projection of it onto every
   * snapshot the runtime decides to write, and the restore below is the inverse.
   *
   * Absent, never zero, exactly as the completed summary's field is (PS5-5).
   */
  /**
   * The repositories **this sitting** writes through, built outside render (P6R14-1).
   *
   * Every governed writer, guarded by one rule: round 13 guarded the mission snapshot — one of the
   * five records a sitting produces — so a named child's stale sitting went on writing skill
   * evidence, progression and its session row after their family had been told all of it was deleted
   * and checked. The unit that has to be guarded is the **bundle**, and the guard itself belongs to
   * the storage layer: it is asked at the commit point, inside the lock a delete also takes, and this
   * file does not get a vote.
   *
   * A function rather than a `useMemo` because the sitting's epoch lives in a ref, and a ref must not
   * be read while rendering. Every caller here is an effect or a handler, which is where writing
   * belongs anyway. Rebuilt only when the underlying bundle changes.
   */
  const scopedRef = useRef<{ base: ThriveRepositories; scoped: ThriveRepositories } | null>(null);
  const sittingRepositories = useCallback((): ThriveRepositories => {
    const cached = scopedRef.current;
    if (cached !== null && cached.base === repositories) return cached.scoped;

    const guarded = scopeRepositoriesToSitting(
      repositories,
      () => sittingEpochRef.current,
      () => sittingGenerationsRef.current,
    );
    const scoped: ThriveRepositories = {
      ...guarded,
      missionProgress: {
        ...guarded.missionProgress,
        saveProgress: (snapshot) => {
          latestSnapshotRef.current = snapshot;
          return guarded.missionProgress.saveProgress(
            parentOverridesRef.current > 0
              ? { ...snapshot, parentOverridesUsed: parentOverridesRef.current }
              : snapshot,
          );
        },
        clearProgress: () => {
          // The body goes with the record (D-054(2)'s rule, applied to this cache): a mission that
          // has just been released must not be writable again by a control pressed afterwards.
          latestSnapshotRef.current = null;
          return guarded.missionProgress.clearProgress();
        },
      },
    };
    scopedRef.current = { base: repositories, scoped };
    return scoped;
  }, [repositories]);
  /**
   * One aggregate count for the sitting, **one place that raises it**, and — since round 12 — one
   * place that makes it durable (PS6-9, P6R1-6, P6R12-1).
   *
   * Round 1 put the increment inside `runParentAction`, the helper the three *bus* commands share,
   * so the two overrides that do not go through the bus were counted by nothing. Round 11 made the
   * count survive a refresh by projecting it onto every snapshot the runtime writes — and those same
   * two overrides are precisely the ones that make the runtime write nothing at all: Restart
   * calibration and Change jump substitute change what the detectors read, and GDS §32 is explicit
   * that the runtime is never told either happened. So the count they raised waited in memory for
   * some later round to carry it, and a tablet closed in that window lost it.
   *
   * Still one counting site and still one projection: the write below is the *same* stamping rule
   * (it goes through `sittingRepositories()`, which stamps), applied to the newest body this stage has
   * seen, at the moment the count changes rather than only when the runtime next writes.
   *
   * **The bound on that claim, stated rather than implied (P6R13-2).** Durability begins at the
   * sitting's **first checkpoint**. A control used before one exists — during the opening round,
   * before any attempt is spent or any round closes — raises the count in memory and has nowhere to
   * put it, so a refresh in that window loses it. That is deliberate: when a sitting becomes
   * resumable is the mission runtime's own decision (PRO-03), and inventing a snapshot here so an
   * observation counter has something to ride would move the resumability boundary — a child would
   * be offered "carry on" for a round nobody has answered yet. The count is an instrument; it does
   * not get to redefine what a saved mission is. Pinned in both directions below.
   *
   * **Ordering.** The repository instance serialises its writes on one queue shared by the shell and
   * the runtime (`withSerialisedWrites`), so this write and the runtime's checkpoints cannot land out
   * of order. And it can never carry a stale tally: every write through the wrapper stamps
   * `parentOverridesRef.current` as it goes, so a checkpoint enqueued after this one carries a count
   * at least as new, and this one carries the newest body (see `latestSnapshotRef`).
   *
   * Aggregate only: no child's name is anywhere near this number, by construction.
   */
  const noteParentOverride = useCallback((): void => {
    parentOverridesRef.current += 1;
    const latest = latestSnapshotRef.current;
    /**
     * Nothing to attach it to yet, and that is the stated bound rather than a gap (P6R13-2).
     *
     * The sitting has no checkpoint, so there is no record this count could ride and none may be
     * manufactured here (see the note above). The tally stays right in memory and the sitting's
     * first checkpoint carries it, because every write through the wrapper stamps the current tally.
     */
    if (latest === null) return;
    void sittingRepositories()
      .missionProgress.saveProgress(latest)
      .catch(() => {
        /**
         * Storage that will not take the checkpoint is a storage problem, never the child's — the
         * mission runtime's own rule for exactly this write (D-052(1)), and it applies with at least as
         * much force to a button a grown-up pressed. The tally stays correct in memory and the next
         * checkpoint carries it; what must not happen is a pause control throwing.
         */
      });
  }, [sittingRepositories]);
  const [parentSummary, setParentSummary] = useState<ParentGameSummary | null>(null);
  /**
   * Whether the grown-up's summary is on screen (D-037(9)).
   *
   * It is always *built* when a mission ends — the button has to be instant, and a build failure
   * must surface either way — but a family that turned the setting off keeps the child's
   * celebration on screen until someone asks for it.
   */
  const [summaryRequested, setSummaryRequested] = useState(false);

  const leave = useCallback(() => {
    void navigate('/catalogue');
  }, [navigate]);

  /** Moves one of the CAM-08 counts. Stable, so the boot effect can hold it without rebuilding. */
  const noteCameraActivity = useCallback(
    (change: (tally: CameraActivityTally) => CameraActivityTally): void => {
      const next = change(cameraActivityRef.current);
      // Reference equality is the signal that nothing happened — a retraction, say — and a render
      // for an unchanged count would be noise in the middle of a five-year-old's mission.
      if (next === cameraActivityRef.current) return;
      cameraActivityRef.current = next;
      setCameraActivity(next);
    },
    [],
  );

  /**
   * Holds or releases the mission for one reason, and tells the runtime only when the *total*
   * changes (Slice E, `pause-causes.ts`).
   *
   * This is the whole of the two-cause composition, and it is deliberately here rather than in the
   * mission runtime: `src/platform/missions` has exactly one paused state and this slice does not
   * add a second. What a family gets from it is two contracts. A grown-up pressing Resume while
   * the camera still cannot see their child lifts *their* hold and nothing else — the mission
   * stays put and the overlay stays up, because resuming into a camera that cannot see is a
   * mission the child cannot play. And a child stepping back into shot does not resume a pause
   * their grown-up is holding, because parent intent outlives a detector.
   *
   * Synchronous on purpose (see `pauseCausesRef`): a recovery decided on a frame must reach the
   * runtime before anything else that frame produces.
   */
  const setPauseCause = useCallback(
    (cause: StagePauseCause, active: boolean): void => {
      const previous = pauseCausesRef.current;
      const next = withPauseCause(previous, cause, active);
      const transition = pauseTransition(previous, next);
      pauseCausesRef.current = next;
      setPauseCauses(next);

      if (transition === 'pause') {
        bridge.emit('shell:pause-requested', {
          reason: cause === 'camera' ? CAMERA_PAUSE_REASON : PARENT_PAUSE_REASON,
        });
      } else if (transition === 'resume') {
        bridge.emit('shell:resume-requested', {
          reason: cause === 'camera' ? CAMERA_PAUSE_REASON : PARENT_PAUSE_REASON,
        });
      }
    },
    [bridge],
  );

  /**
   * Replay (C-8). The parent's session choices are kept; only the curated-sequence flag changes,
   * so the second playthrough draws from the bank instead of repeating the fixed sequence.
   * Writing the config back into the shell holder is what restarts the stage: the boot effect
   * reads it, so a new config is a new run.
   *
   * "Again" means from the beginning. A sitting a grown-up ended early leaves its snapshot behind
   * on purpose (game design spec §32) and the boot effect would resume it, so this clears it
   * first — a no-op after a mission that finished, since completion already cleared it. Carrying
   * on is Home's "Continue playing" card, which says so; this button says something else.
   */
  const playAgain = useCallback(() => {
    setCompletion(null);
    setParentSummary(null);
    setSummaryRequested(false);
    /**
     * A new sitting starts with no help behind it (P6R1-7, D-041 fresh-run semantics).
     *
     * The count describes *this evening*, and round 1 reset the mission around it while leaving the
     * tally standing — so a family who used one override, finished, and played again without
     * touching the controls had the second sitting's record say a grown-up had stepped in. That is
     * an observation instrument reporting something that did not happen, which is worse than not
     * reporting at all. Reset at the same boundary as everything else the sitting owns.
     */
    parentOverridesRef.current = 0;
    setParentOverridesUsed(null);
    missionEndedRef.current = false;
    setMissionEnded(false);
    setRound(null);
    setAssist(null);
    setShards(0);
    setStageError(null);

    void (async () => {
      try {
        /**
         * Through the wrapper, because the release has to reach the cached body too (P6R12-1).
         *
         * The runtime's own clear already did — a finished mission releases its snapshot through
         * this seam — but the shell has a second clear, and it is the one that runs for the sitting
         * that matters here: a sitting a grown-up **ended early** keeps its snapshot, so this is
         * where that record is released. Clearing it on the raw repository left the stage still
         * holding the body, and the first camera-only control pressed in the new sitting wrote it
         * back: Home would then offer to carry on with a mission that was over, under the ended
         * sitting's session id. One clear path, so the cache cannot disagree with the record.
         */
        await sittingRepositories().missionProgress.clearProgress();
        // The identity goes with the snapshot it describes (D-054(2)): leaving it behind would
        // let the next launch reconstruct a sitting whose mission has just been released.
        await sittingRepositories().launchIdentity.clear();
      } catch (cause) {
        // Said out loud rather than swallowed: a snapshot that would not clear means the run
        // about to start is a carried-on one, and a grown-up who asked for a fresh start should
        // be told why they did not get one.
        setStageError(cause instanceof Error ? cause.message : String(cause));
      }
      setSessionLaunchConfig({ ...launchConfigRef.current, useCuratedSequence: false });
    })();
  }, [sittingRepositories, setSessionLaunchConfig]);

  // React side of the bridge. Subscribing here — not inside the stage effect — keeps these
  // listeners alive across a stage rebuild.
  useEffect(() => {
    const unsubscribes = [
      bridge.on('game:scene-ready', () => {
        setSceneReady(true);
      }),
      bridge.on('game:input-observed', ({ lines }) => {
        setFeedLines(lines);
      }),
      bridge.on('game:assets-resolved', ({ resolutions }) => {
        setAssetReports(resolutions);
      }),
      bridge.on('game:paused', () => {
        // Every pause path arrives here — the grown-up's button, the P key, a camera that cannot
        // see, the start of a mid-mission re-training — so this is the one place the camera has to
        // be told that nothing it sees from now on can be an answer (S1-3).
        neutralizeCameraRef.current?.();
        // The attempt window closed, so any unreadable attempt inside it is over (S3-3): whatever
        // the child tries after Resume is a new attempt, not the same one still going.
        noteCameraActivity(withUncertaintyCleared);
        setPaused(true);
      }),
      bridge.on('game:resumed', () => {
        /**
         * The other edge of the pause boundary (S2-3).
         *
         * Discarding on the way *in* is not enough. The detectors keep reading the frames that
         * arrive behind the overlay — as they must, or a held posture would fall out of step with
         * the keyboard's latches — so a couple of neutral frames reopen the neutral gate and the
         * child is free to begin a whole new action whose *completion* lands in the running
         * runtime. On the keyboard that cannot happen: the `keydown` that is the onset is consumed
         * while input is being ignored, and the `keyup` says nothing. So the movement in progress at
         * the moment the mission starts listening again is discarded too.
         */
        neutralizeCameraRef.current?.();
        // ...and the same on the way back: see the pause handler above (S3-3).
        noteCameraActivity(withUncertaintyCleared);
        setPaused(false);
      }),
      bridge.on('game:exit-acknowledged', () => {
        leave();
      }),
      /**
       * The P key (game design spec §30) asks for a pause without going through any control on
       * screen, so its request is adopted as the grown-up's hold — otherwise the mission would be
       * paused with nobody recorded as holding it, and the Resume button would have nothing to
       * lift. The stage's own requests are skipped by reason, or this would recurse.
       */
      bridge.on('shell:pause-requested', ({ reason }) => {
        if (STAGE_PAUSE_REASONS.has(reason)) return;
        setPauseCause('parent', true);
      }),
      bridge.on('game:error', ({ code }) => {
        setStageError(code);
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [bridge, leave, noteCameraActivity, setPauseCause]);

  // The readout above, kept current. Unsubscribes with the stage, so a stale listener cannot
  // outlive the screen that renders it.
  /**
   * Every sound that has been heard during this stage's life (R2-12).
   *
   * `data-audio-playing` is instantaneous, and an automated browser can only *sample* it — so a cue
   * that starts and ends between two samples is audible and unobserved, and an absence assertion
   * built on sampling says nothing about the gaps (the D-100 transient family). This accumulates,
   * so "nothing on the sound bus ever played" is a single read at the end of a journey rather than
   * a hope about polling intervals.
   *
   * Bounded by the theme: it can only ever hold IDs the manifest declares. Same grown-up diagnostic
   * register as the attributes beside it — no new words on screen.
   */
  useEffect(
    () =>
      audio.subscribe((state) => {
        setAudioState(state);
        setAudioHeard((heard) =>
          // Same array when nothing is new, so a readout cannot cause a render on every tick.
          state.playing.every((audioId) => heard.includes(audioId))
            ? heard
            : [...new Set([...heard, ...state.playing])],
        );
      }),
    [audio],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    let cancelled = false;
    let teardownRun = false;
    // Aborts an in-flight module preload if the stage unmounts before it finishes.
    const abort = new AbortController();

    // A new run is a new sitting, and CAM-08 counts a sitting (Slice H). "Play again" rebuilds the
    // stage through here, so the counts a grown-up reads always describe the mission in front of
    // them rather than accumulating across the evening.
    /**
     * …and the same is true of the erasure boundary (P6R14-4).
     *
     * This effect is what starts a sitting — a fresh mount, a resume, or "Play again" handing back a
     * new config — so it is where a sitting learns which side of a parent's delete it was born on.
     * Captured here rather than at mount, because a run that begins *after* a delete is nobody's
     * stale sitting and is entitled to keep what a child earns in it.
     */
    sittingEpochRef.current = currentErasureEpoch();
    // A new run is a new set of children to be about, until the roster below says otherwise.
    sittingGenerationsRef.current = new Map();
    latestSnapshotRef.current = null;
    missionEndedRef.current = false;
    roundOpenRef.current = false;
    cameraActivityRef.current = NO_CAMERA_ACTIVITY;
    setCameraActivity(NO_CAMERA_ACTIVITY);
    setSummaryCameraQuality(null);

    /**
     * Disposers, newest first. The stack is built up as resources are acquired and is installed
     * *before* anything can fail, so mission-loading failure, preload rejection and unmounting
     * mid-preload all reach the same teardown as a normal exit. Installing it only once the
     * Phaser host existed left those three paths never calling `module.dispose()`.
     */
    const disposers: (() => void)[] = [];

    /** Runs the stage teardown at most once, whichever path gets there first. */
    const runTeardown = (): void => {
      if (teardownRun) return;
      teardownRun = true;
      while (disposers.length > 0) {
        try {
          disposers.pop()?.();
        } catch {
          // Teardown must not fail: a disposer that throws cannot stop the others running.
        }
      }
    };

    // From this point the stage owns the module for its lifetime, so every exit path releases
    // it — including the ones that never get as far as showing anything.
    disposers.push(() => {
      void Promise.resolve(gameModule.dispose?.()).catch(() => {
        // A module that fails to dispose is logged by the module, not surfaced to the child.
      });
    });
    // Likewise: nothing this stage started saying should outlive it.
    disposers.push(() => {
      narration.stop();
    });

    /**
     * …and nothing it started *playing* either (Phase 5 Slice 0″).
     *
     * Built here, beside the narration disposer and on the same stack, so that every exit path
     * this stage has — a normal leave, a failed preload, a scene constructor that throws, an
     * unmount mid-boot — stops the music. A bed still playing over an empty stage is a sound a
     * family has no way to switch off.
     *
     * It is constructed before the runtime so that `MISSION_STARTED` — the first event the runtime
     * emits — already has somewhere to land.
     */

    /**
     * Everything the boot sequence can throw, including what the inner catches do not cover:
     * a runtime chunk that fails to download, a Phaser host that cannot get a WebGL context,
     * a scene constructor that objects to its own arguments. Any of those used to reject an
     * ignored promise — no teardown, no message, and a child left looking at an empty box.
     */
    const reportBootFailure = (cause: unknown): void => {
      if (!cancelled) {
        setStageError(cause instanceof Error ? cause.message : String(cause));
      }
      runTeardown();
    };

    void (async () => {
      /**
       * What a grown-up chose at setup — or, after a reload, the sitting reconstructed from the
       * launch-identity record (D-054(2)). A deep link with nothing recorded still computes
       * exactly what the setup screen would have shown, rather than falling back to a different,
       * quieter set of assumptions.
       *
       * The same helper answers Home's "carry on" card, so the offer and the decision cannot
       * drift apart (D-046(4)).
       */
      const sitting = await resolveSitting(repositories, {
        gameId: gameModule.id,
        sessionLaunchConfig,
        activePlayer: activePlayer ?? resolvedPlayerRef.current,
      });
      const config = sitting.config;
      if (cancelled) {
        runTeardown();
        return;
      }
      launchConfigRef.current = config;
      resolvedPlayerRef.current = sitting.player;

      // Before anything can speak: the mission's first instruction is narrated the moment it starts.
      narration.setEnabled(config.narrationEnabled);
      narration.setVolume(config.narrationVolume);

      /**
       * …and before anything can *sound* (R1-2, PS5-5).
       *
       * The setup screen applies these on its way out, but a sitting can reach this stage without
       * passing it — a reload onto Home and "Carry on", or a deep link. The config here is rebuilt
       * from the same family record by the same resolver, so this is the one other place the
       * family's choices can be honoured. Without it a family who turned the music down got it back
       * at full for the whole resumed mission, and a muted family got a bed.
       *
       * Placed with the narration lines, above the runtime: `MISSION_STARTED` is what starts the
       * music, and it cannot have been emitted yet. Nothing here unlocks anything — that is a
       * browser gesture, and it belongs to the activation the grown-up performed to get here.
       */
      audio.setEnabled(config.soundEnabled);
      audio.setVolume(config.soundVolume);

      /**
       * Everything the mission and its scenes say goes through here (NAR-01/02). It is a
       * pass-through: it decides nothing, and it exists so the readout can report a repeat
       * request that a silent device would otherwise leave no evidence of.
       */
      const voice = observeNarration(narration, (activity) => {
        if (!cancelled) setNarrationActivity(activity);
      });
      setNarrationActivity({ utterances: 0, lastText: null, history: [] });

      const players: PlayerRuntimeState[] = buildSessionPlayers(config, sitting.player);

      /**
       * Is this a camera sitting, and may it be (CAM-04, architecture §9's "check reusable
       * calibration")?
       *
       * Two things have to be true. A grown-up (or a child finishing hero training) asked for the
       * camera, **and** this sitting holds a calibration that may be reused for it. A camera
       * without a calibration would read a five-year-old's body against numbers measured on
       * nobody, so the sitting stays at the keyboard rather than pretending — and says so in the
       * readout instead of degrading silently.
       *
       * **Keyed by the child, not by the lane.** Hero training saves under the identity the shell
       * knows (`ActivePlayer.playerId`, a profile id or the guest slot); the semantic bus speaks in
       * lane ids. Both are correct for their own job — a calibration is a fact about a body, a lane
       * is a seat at this mission — so the lookup uses the training key and the adapter speaks for
       * the seat.
       */
      const calibrationPlayerId = sitting.player?.playerId ?? HERO_1_PLAYER_ID;
      /**
       * The play space this sitting was set up for (D-059). A sitting that reached the stage
       * without a setup screen — a deep link, a reload — gets the same flagged default the setup
       * screen would have shown, because the config it is reading is the one that screen produces.
       */
      const cameraMode = config.cameraMode;
      /**
       * Architecture §20's capability rule, finally enforced (D-064(5)'s named follow-up).
       *
       * Slice D passed an empty list here with a note explaining why: refusing the camera because a
       * child could not jump today was worse than the substitute GDS §15 promises them, and the
       * substitute did not exist yet. It does now — so the requirement is real, and it is the
       * *room's* translation of what the game declared: a Compact sitting requires the arms it
       * counts with, and never a jump.
       */
      const resolveCalibration = () =>
        cameraInputEnabled
          ? camera.calibration.resolve({
              playerId: calibrationPlayerId,
              viewport: { width: window.innerWidth, height: window.innerHeight },
              requiredCapabilities: requiredCameraCapabilities(
                gameModule.launchRequirements.requiredCapabilities,
                cameraMode,
              ),
              cameraMode,
              /**
               * Which camera this sitting is looking through, when it is known (S1-7).
               *
               * Read at the moment the question is asked rather than captured once, because the
               * stream opens *after* the boot effect first resolves: the launch answer is
               * re-asked by `revalidateCameraRef` as soon as the mission's stream is live.
               * Session-only, never persisted.
               */
              ...(camera.activeCameraDeviceId === null
                ? {}
                : { cameraDeviceId: camera.activeCameraDeviceId }),
            })
          : null;
      /**
       * Two heroes answering with two bodies (Slice D′, CAM-07).
       *
       * The shipped guard, asked twice: *a grown-up asked for the camera, and this sitting holds a
       * calibration that may be reused for it*. In a cooperative sitting the two questions are asked
       * on the two fixed hero ids — which is exactly what the cooperative trainer saves under
       * (D-080(3)) — and since Slice F′ the answer is **per child** (D-076(5), retiring D-081(2)'s
       * all-or-nothing interim). A hero who holds a reusable profile plays with their body; a hero
       * who does not plays with their keys; both are in the same sitting, answering the same rounds,
       * against the same untouched runtime. The parity architecture is what makes this cheap:
       * every seat has had its own input path since Phase 2, so allowing the combination is less
       * code than refusing it.
       *
       * `full-motion` regardless of the launch config, because cooperative camera play is
       * Full-Motion only (D-076(4)). Parent Setup already removes Compact from a two-hero sitting;
       * this is what stops a deep link smuggling it back in.
       */
      const cooperativeSitting = config.playerMode === 'COOP_SIMULTANEOUS_SIMPLE';
      const resolveLane = (playerId: PlayerId) =>
        cameraInputEnabled
          ? camera.calibration.resolve({
              playerId,
              viewport: { width: window.innerWidth, height: window.innerHeight },
              requiredCapabilities: requiredCameraCapabilities(
                gameModule.launchRequirements.requiredCapabilities,
                'full-motion',
              ),
              cameraMode: 'full-motion',
              ...(camera.activeCameraDeviceId === null
                ? {}
                : { cameraDeviceId: camera.activeCameraDeviceId }),
            })
          : null;

      const laneResolutions = cooperativeSitting
        ? { left: resolveLane(HERO_1_PLAYER_ID), right: resolveLane(HERO_2_PLAYER_ID) }
        : null;
      /**
       * Each lane's own answer, independently (Slice F′).
       *
       * `null` for a hero who is playing on the keyboard. Note the pair is still built whenever
       * **either** lane has a profile: the resolver has to keep seeing both bodies or the camera
       * lane loses the separation floor that stops the sibling's body being read as theirs.
       */
      const laneProfiles =
        laneResolutions === null
          ? null
          : {
              left: laneResolutions.left?.profile ?? null,
              right: laneResolutions.right?.profile ?? null,
            };
      const anyLaneOnCamera =
        laneProfiles !== null && (laneProfiles.left !== null || laneProfiles.right !== null);

      // A cooperative sitting never asks the single-player question: one adapter reading one child
      // in a two-child room is precisely the half-state this guard exists to refuse.
      const calibrationResolution = cooperativeSitting ? null : resolveCalibration();
      const calibration = calibrationResolution?.profile ?? null;
      /**
       * Who and what a mid-mission `RESTART_CALIBRATION` would train (Slice G).
       *
       * Recorded in state because the control that offers it lives outside this effect, and because
       * a sitting the camera was refused for (`CAPABILITY_MISSING`) must still be able to reach it —
       * that refusal is precisely the case D-066(5) says this override exists to recover.
       */
      if (!cancelled) {
        /**
         * The single-player half, unchanged: non-null in every sitting a grown-up asked the camera
         * for, including one architecture §20 refused a profile for — which is exactly the sitting
         * D-066(5) says `RESTART_CALIBRATION` exists to recover.
         *
         * A cooperative sitting is gated on `cameraLaneHeroes` instead (below), because there its
         * controls have to name a hero and only heroes already on the camera may be named: bringing
         * a keys hero onto the camera mid-sitting is a different flow, deliberately not built.
         */
        setCameraSetup(
          cameraInputEnabled && !cooperativeSitting
            ? { playerId: calibrationPlayerId, mode: cameraMode }
            : null,
        );
      }
      const cameraSitting = cooperativeSitting ? anyLaneOnCamera : calibration !== null;
      if (!cancelled) {
        setCooperativeCameraSitting(cooperativeSitting && cameraSitting);
        setKeyboardElectedHeroes([]);
        // The seat count first, so the sitting's very first coverage observation is measured
        // against a roster rather than against nobody (S7).
        seatedHeroCountRef.current = players.length;
        noteCameraLaneHeroes(
          cooperativeSitting
            ? [
                ...(laneProfiles?.left === null ? [] : [HERO_1_PLAYER_ID]),
                ...(laneProfiles?.right === null ? [] : [HERO_2_PLAYER_ID]),
              ]
            : calibration === null
              ? []
              : [calibrationPlayerId],
          // A new sitting: the coverage history starts again, with these seats.
          true,
        );
        setRetrainingPlayerId(null);
      }
      /**
       * Why the camera is not answering, when a grown-up asked for it and it is not.
       *
       * Reported rather than swallowed: architecture §20's answer to an uncovered capability is
       * "recalibrate", and a family that is never told cannot. `NO_PROFILE` is excluded because it
       * is not a refusal — it is a child who has not been to hero training yet, which the readiness
       * and calibration screens already say plainly.
       */
      /**
       * In a cooperative sitting the refusals reported are the ones belonging to lanes that did
       * **not** get a camera (Slice F′). A hero happily playing with their body has nothing to
       * explain, and a hero whose profile was refused deserves the explanation even when their
       * sibling's was fine — which the all-or-nothing guard could never express.
       *
       * It stays one sitting-wide notice carrying refusal *codes* and naming nobody: per-child
       * quality surfaces are what D-076(6) forbids, and a code is not one.
       */
      const calibrationRefusals = (
        cooperativeSitting
          ? [
              ...(laneProfiles?.left === null ? (laneResolutions?.left?.refusals ?? []) : []),
              ...(laneProfiles?.right === null ? (laneResolutions?.right?.refusals ?? []) : []),
            ]
          : (calibrationResolution?.refusals ?? [])
      ).filter((refusal: CalibrationReuseRefusal) => refusal !== 'NO_PROFILE');
      if (!cancelled) {
        setCameraRefusals(
          cooperativeSitting ? calibrationRefusals : cameraSitting ? [] : calibrationRefusals,
        );
      }
      const sessionInputMode: SessionInputMode = cameraSitting ? 'MIXED' : 'KEYBOARD';
      if (!cancelled) setInputMode(sessionInputMode);

      /**
       * Who launched, recorded before the mission starts (D-054(2)).
       *
       * Before rather than beside the first snapshot, deliberately. A crash between the two writes
       * leaves either an identity with no mission — harmless, cleared as stale on the next launch
       * — or a mission with no identity, which the guard refuses and which costs a child the
       * rounds they played. Only one of those orders puts the cheap failure first.
       *
       * Guests included: a guest sitting is an identity too (the guest slot), and recording it is
       * what keeps the restore path one path rather than two.
       */
      try {
        /**
         * Each named seat records **which generation it was chosen at** (S1-8, P6R17-1).
         *
         * Profile ids are caller-supplied and `saveChild` upserts on them, so an id alone cannot
         * tell child A from a child B created under the same id after A was deleted. The generation
         * is `createdAt`, and it is **carried from the selection** — through `ActivePlayer` and the
         * cooperative launch config — rather than looked up here.
         *
         * Round 16 did look it up here, and that read was the defect: it can only answer "who holds
         * this id at this instant", so a delete-and-recreate between choosing a child and booting
         * the stage made the sitting adopt the replacement child as its own. The sitting's identity
         * is who was *chosen*; there is no moment later at which the store can be asked that.
         *
         * A named seat that arrives with no generation is left without one, and the storage layer
         * fails closed on it rather than guessing.
         */
        const seats = players.map((player) => {
          const generation =
            player.playerId === players[0]?.playerId
              ? sitting.player?.profileCreatedAt
              : config.secondPlayer?.profileCreatedAt;
          return {
            playerId: player.playerId,
            band: player.band,
            isGuest: player.isGuest,
            ...(player.profileId === undefined ? {} : { profileId: player.profileId }),
            ...(player.profileId === undefined || generation === undefined
              ? {}
              : { profileCreatedAt: generation }),
          };
        });

        sittingGenerationsRef.current = new Map(
          seats.flatMap((seat) =>
            'profileId' in seat && seat.profileCreatedAt !== undefined
              ? [[seat.profileId, seat.profileCreatedAt] as const]
              : [],
          ),
        );

        await sittingRepositories().launchIdentity.save({
          gameId: gameModule.id,
          playerMode: config.playerMode,
          players: seats,
        });
      } catch {
        // A sitting that could not record who is playing is still a sitting a child can play.
        // The cost is that a refresh will start it again, which is exactly the old behaviour.
      }
      if (cancelled) {
        runTeardown();
        return;
      }

      const listContext = {
        players,
        playerMode: config.playerMode,
        availableCapabilities: KEYBOARD_CAPABILITIES,
      };

      let mission: MissionDefinition;
      // An unfinished mission is resumed rather than restarted, and it keeps its own session id
      // so the rounds it already scored stay scored (PRO-03).
      let resumeFrom = await repositories.missionProgress.getProgress();
      let sessionId: string = crypto.randomUUID();
      /**
       * Which sequence this sitting is playing (STOP-1, D-042(5)).
       *
       * The launch config is session-only and a refresh recomputes it as the curated default, so
       * a replay resumed after a refresh would be built for the *curated* sequence while carrying
       * on at the replay's own round index — the same positions asked again, on top of a restored
       * score. The snapshot records its own provenance, and on a resume that is what governs.
       * Both the mission build and the runtime read this one value, because two sources that can
       * disagree eventually will.
       */
      let sittingUsesCuratedSequence = config.useCuratedSequence;

      try {
        const [summary] = await gameModule.missionProvider.listMissions(listContext);
        if (summary === undefined) throw new Error('The game offers no missions.');
        // A saved mission belongs to the child who was playing it. Handing it to whoever launches
        // next would give a sibling another child's score and spend their teaching moment — and
        // would write the rest of the mission's evidence against the wrong child.
        if (
          canResumeMissionProgress(resumeFrom, {
            missionId: summary.id,
            players: players.map((player) => ({
              playerId: player.playerId,
              band: player.band,
              ...(player.profileId === undefined ? {} : { profileId: player.profileId }),
            })),
          }) &&
          resumeFrom !== null
        ) {
          sessionId = resumeFrom.sessionId;
          sittingUsesCuratedSequence = resumeFrom.useCuratedSequence;
          /**
           * The help this sitting had already been given, back where it belongs (P6R11-1).
           *
           * The inverse of the projection above, and restored *only here* — inside the one branch
           * that has already proved this snapshot is this sitting. A launch the guard refuses is a
           * new sitting and inherits nothing, and "Play again" resets the same count at the same
           * boundary it resets everything else the sitting owns (D-107). A snapshot from a build
           * that never counted says nothing, which reads as nothing rather than as zero.
           */
          parentOverridesRef.current = resumeFrom.parentOverridesUsed ?? 0;
          /**
           * …and the body it came back on, so help given *before* this sitting's first checkpoint is
           * durable too (P6R12-1). Without this, a family who resumed and immediately changed the
           * play space would be back in the window round 12 exists to close.
           */
          latestSnapshotRef.current = resumeFrom;
        } else {
          resumeFrom = null;
        }
        mission = await gameModule.missionProvider.getMission(summary.id, {
          ...listContext,
          sessionId,
          useCuratedSequence: sittingUsesCuratedSequence,
          tunables: tunables.get(),
        });
      } catch (cause) {
        if (!cancelled) {
          setStageError(cause instanceof Error ? cause.message : String(cause));
        }
        runTeardown();
        return;
      }

      if (cancelled) {
        runTeardown();
        return;
      }

      // Phaser is loaded only when a stage actually mounts. That keeps the engine out of the
      // shell's bundle (and out of jsdom, which has no canvas) until it is genuinely needed.
      const [
        { createPhaserHost },
        { StageChromeScene, STAGE_CHROME_SCENE_KEY },
        { StageFallbackScene },
      ] = await Promise.all([
        import('../../../platform/runtime/phaser-host'),
        import('../../../platform/runtime/stage-chrome-scene'),
        import('../../../platform/runtime/stage-fallback-scene'),
      ]);
      if (cancelled) {
        runTeardown();
        return;
      }

      const assets = createThemeAssetResolver(gameModule.themeManifest);

      // The documented launch lifecycle is Loading → Validating → Preloading → Ready (module
      // spec §22). Validation happened in the registry; this is the preload step, and skipping
      // it would leave the hook permanently dead code.
      try {
        await gameModule.preload?.({
          assets,
          onProgress: (fraction) => {
            if (!cancelled) setPreloadProgress(fraction);
          },
          signal: abort.signal,
        });
      } catch (cause) {
        if (!cancelled) setStageError(cause instanceof Error ? cause.message : String(cause));
        runTeardown();
        return;
      }
      if (cancelled) {
        runTeardown();
        return;
      }

      const bus = createSemanticEventBus();
      const reader = createSemanticInputReader(bus);

      /**
       * The sitting's own keys, bound once for the whole stage (D-045 PS2-5).
       *
       * The app bus is what makes R and P live: they are shell requests, not player actions, so
       * they travel on the React↔Phaser bridge rather than the semantic input bus (D-035). F is
       * the stage's single freeze — every hero's adapter reads the same gate, so one press
       * silences the room rather than one child.
       */
      const sessionControls = createKeyboardSessionControls({ appEvents: bridge });

      /**
       * One adapter per hero seated in this session, and none for a hero who is not.
       *
       * Per hero rather than one adapter serving two mappings, because every piece of state
       * D-035 settled — the latched zone, the held crouch, the raised hands, the debounced
       * action — is that child's own. Keeping one instance per child makes that separation
       * structural instead of a keyed map somebody has to remember to key correctly, and it
       * means Hero 2's keys are exercised through exactly the same surface as Hero 1's.
       */
      const keyboards = players.map((player) =>
        createKeyboardInputAdapter({
          bus,
          playerId: player.playerId,
          tunables: tunables.get(),
          freeze: sessionControls.freeze,
        }),
      );
      if (!cancelled) {
        setCoopKeyHelp(players.length > 1);
        // What each hero is called, from the roster this stage actually built (Slice E′). A
        // cooperative invitation is addressed to a child by their own name or their own seat —
        // never to one child *about* the other.
        setHeroLabels(
          Object.fromEntries(players.map((player) => [player.playerId, playerLabel(player)])),
        );
      }

      disposers.push(() => {
        for (const keyboard of keyboards) keyboard.detach();
        sessionControls.detach();
        reader.dispose();
        bus.clear();
      });

      /**
       * The runtime's event stream, made subscribable by scenes that are created *by* it.
       * The director starts a scene on `STAGE_ENTERED`; Phaser boots it a tick later, so the
       * relay replays the situation that scene is arriving into (module spec §9).
       */
      const relay = createMissionEventRelay();
      disposers.push(() => {
        relay.dispose();
      });

      /**
       * The sitting's one sound owner, watching the events this stage publishes (R4-5).
       *
       * Built here rather than beside the audio service because it is an *observer*: it takes the
       * relay and subscribes, and this stage never holds anything callable that makes a noise. The
       * relay's fan-out isolates every observer, so a sound that throws costs the sound and nothing
       * else — which is the whole of the S0-R1 promise the old hand-written guard was making.
       */
      const stageAudio = createStageAudio(audio, gameModule.themeManifest, relay);
      disposers.push(() => {
        stageAudio.dispose();
      });

      // Declared before the runtime because the runtime's own `onEvent` drives it, and assigned
      // straight after the Phaser host exists. Events that arrive before then belong to no stage.
      let director: StageSceneDirector | null = null;

      const runtime = createMissionRuntime({
        mission,
        gameId: gameModule.id,
        content: gameModule.contentProvider,
        scoring: gameModule.scoringStrategy,
        teaching: gameModule.teachingProvider,
        // The badge/shard persistence path (D-039(5), R1-11). The channel existed; nothing in
        // production had ever called it, so a child's completed mission, shards and best badge
        // vanished the moment they left the page.
        progression: gameModule.progressionProvider,
        // The module's bounded adaptation policy (R1-13, SKL-04). It was a provider nobody called,
        // so every item came from the statically configured band.
        adaptation: gameModule.adaptationPolicy,
        session: {
          sessionId,
          gameId: gameModule.id,
          missionId: mission.id,
          playerMode: config.playerMode,
          inputMode: sessionInputMode,
          startedAt: resumeFrom?.startedAt ?? new Date().toISOString(),
        },
        players,
        playerMode: config.playerMode,
        // "Play again" hands the same session config back with this turned off, which is what
        // makes the second playthrough draw from the replay bank (D-037(8), GLP-02) — and on a
        // resume the snapshot's own provenance governs instead, from the one value above.
        useCuratedSequence: sittingUsesCuratedSequence,
        tunables: tunables.get(),
        input: reader,
        narration: voice,
        // The same repositories, with the sitting's override tally projected onto every snapshot
        // the runtime writes (P6R11-1). Every other repository, and every other call, is untouched.
        repositories: sittingRepositories(),
        onEvent: (event) => {
          publishRuntimeEvent(bridge, event);
          // Scenes before the shell: the relay records the situation and fans out, then the
          // director swaps the stage that is on screen.
          relay.publish(event);
          director?.handle(event);
          if (cancelled) return;

          switch (event.type) {
            case 'ROUND_PRESENTED':
              // A question is on screen from here until it is resolved (S2-6), and from here any
              // camera wired into this sitting is joining a mission already under way (S5-2).
              roundOpenRef.current = true;
              missionUnderWay = true;
              // A new question is a new attempt (S3-3).
              noteCameraActivity(withUncertaintyCleared);
              // Between rounds, never mid-round: a switch releases held postures and clears the
              // latched zone, which would retract an answer a child is still holding (D-035).
              // Both heroes switch together — the mechanic on screen is the same for both.
              for (const keyboard of keyboards) {
                keyboard.setInputContext(inputContextFor(event.prompt.inputMethod));
              }
              // The camera switches on the same event, from the same function, with the same
              // release-under-the-old-meaning semantics (D-035/D-062(4)). Two rules that could
              // drift would eventually let one input method answer what the other could not.
              inputContextNow = inputContextFor(event.prompt.inputMethod);
              cameraInput?.setInputContext(inputContextNow);
              // Both lanes switch on the same event, from the same value: the mechanic on screen is
              // the same for both heroes, and two rules that could drift eventually would.
              coopCamera?.setInputContext(inputContextNow);
              setRound({
                stageId: event.stageId,
                contentId: event.contentId,
                promptText: event.prompt.text,
                attemptNumber: event.attemptNumber,
                simplified: event.simplified,
                choiceLabels: (event.prompt.choices ?? []).map((choice) => choice.label),
              });
              setAssist(null);
              break;
            case 'ROUND_ASSIST':
              setAssist(event.text ?? event.assist);
              break;
            case 'ROUND_RESOLVED':
              // Answered, or moved past. Either way nothing on screen is asking anything until the
              // next round is presented (S2-6), and whatever was unreadable is over (S3-3).
              roundOpenRef.current = false;
              noteCameraActivity(withUncertaintyCleared);
              break;
            case 'SCORE_CHANGED':
              setShards(event.score.value);
              break;
            case 'MISSION_COMPLETED':
              setRound(null);
              roundOpenRef.current = false;
              missionEndedRef.current = true;
              setMissionEnded(true);
              // Nothing is running, so there is nothing left to pause: the grown-up's overlay
              // goes with the mission rather than sitting over the end of it. The camera's
              // reasons go the same way — a finished mission is not waiting for anybody to step
              // back into shot.
              setPaused(false);
              pauseCausesRef.current = [];
              setPauseCauses([]);
              setRecovery(CAMERA_HEALTHY);
              setLaneRecovery([]);
              // D-037(9): the child's celebration stays up unless a grown-up asked for the
              // summary to follow the mission automatically.
              setSummaryRequested(config.showParentSummaryAfterMission);
              // The whole finished session, taken from the runtime event itself: the
              // `game:completed` bridge event carries only the summary, and the game's parent
              // summary adapter needs the record and the evidence as well.
              setCompletion({
                session: event.session,
                reward: event.reward,
                endedEarly: event.endedEarly,
              });
              break;
            default:
              break;
          }
        },
      });

      disposers.push(() => {
        runtime.dispose();
      });

      /**
       * The child's body, on the same bus as the keys (CAM-04, SDK-05).
       *
       * Built here rather than beside the keyboards for one reason: the uncertainty sink is the
       * runtime's own analytics writer, and D-059 PS3-1 makes that sink the *whole* of how a
       * detector reports that it could not see. An adapter without it would silently drop the one
       * signal that keeps "the camera could not see you" from ever becoming "you were wrong".
       *
       * Everything else about it is deliberately unremarkable: one adapter for the hero in the
       * left lane, fed by the camera session's own frames (D-063(4) — there is exactly one
       * detection loop in the app), reading this child's calibrated thresholds, emitting the same
       * ten events into the same bus the keyboard uses. Nothing downstream knows it exists.
       */
      let cameraInput: CameraInputAdapter | null = null;
      let recoveryMonitor: TrackingRecoveryMonitor | null = null;
      /**
       * What the round on screen currently means, and which movement this room counts with.
       *
       * Held here because a camera rebuilt mid-mission (a grown-up's `RESTART_CALIBRATION`) must
       * arrive into the round that is actually running, not into the one the sitting started on.
       */
      /**
       * Whether the camera is starting to watch a mission that is **already under way** (S5-2).
       *
       * True from the outset for a resumed sitting — a child who left an incomplete mission and
       * pressed Continue is mid-round from the first frame — and true for every adapter built after
       * the first round has been presented, which is how the `CAPABILITY_MISSING` recovery path
       * gets it too. The adapter uses it to decide whether its first detector set observes the
       * child's current posture as an answer or as a baseline.
       */
      let missionUnderWay = resumeFrom !== null;
      let inputContextNow: PoseInputContext = DEFAULT_POSE_INPUT_CONTEXT;
      let substituteNow: JumpSubstitute = config.jumpSubstitute;

      /**
       * Builds — or rebuilds, or removes — the camera half of the input chain.
       *
       * The ordering inside is load-bearing rather than tidy: the monitor subscribes to frames
       * **before** the adapter attaches, so on any given frame it sees the situation before the
       * detectors read it. A frame that brings the child back therefore resumes the mission before
       * anything that same frame detects could be dropped as an answer given while paused.
       */
      /** Unsubscribes the adapter's own starvation listener, when one is attached. */
      let releaseStarvationWatch: (() => void) | null = null;
      /** The cooperative pair, in a two-hero camera sitting and nowhere else (Slice D′). */
      let coopCamera: CooperativeCameraAdapter | null = null;
      /** Its recovery watch: two shipped monitors composed by the two D-076(2) rulings (Slice E′). */
      let coopRecoveryMonitor: CooperativeTrackingRecoveryMonitor | null = null;

      /**
       * Everything the camera half of this sitting holds, released.
       *
       * One function since Slice D′ because there are now two shapes of adapter and three callers
       * that must let go of *whichever* is attached — rebuilding, the stage's own teardown, and a
       * grown-up electing the keyboard. Three copies of that list is three chances for one of them
       * to leave a lane latched.
       */
      const releaseCameraChain = (): void => {
        recoveryMonitor?.dispose();
        recoveryMonitor = null;
        coopRecoveryMonitor?.dispose();
        coopRecoveryMonitor = null;
        releaseStarvationWatch?.();
        releaseStarvationWatch = null;
        cameraInput?.detach();
        cameraInput = null;
        coopCamera?.detach();
        coopCamera = null;
        // A sitting with no pair has nothing to borrow an identity verdict from, and a cooperative
        // retraining must not be offered — let alone mounted — without one (S3).
        setRetrainingLaneView(null);
        neutralizeCameraRef.current = null;
      };

      /**
       * The window in which a movement could be an attempt to answer (S1-8, S2-6).
       *
       * `pauseCausesRef` and `roundOpenRef` rather than the React state for the usual reason: the
       * decision has to be synchronous, and a movement made a microsecond into a pause — or a
       * microsecond after a round resolved — is still outside the window.
       *
       * Hoisted out of `wireCamera` by Slice D′ so the cooperative pair is measured by the *same*
       * window as a single child, rather than by a second definition that could drift from it.
       */
      const attemptWindow = (): CameraActivityContext | null =>
        missionEndedRef.current
          ? null
          : {
              running: pauseCausesRef.current.length === 0,
              inputContext: inputContextNow,
              roundOpen: roundOpenRef.current,
            };

      /**
       * The CAM-08 taps, wrapped once for whichever adapter this sitting builds (Slice H, D′).
       *
       * They wrap the two things the *camera* adapter is given — its bus and its uncertainty sink —
       * rather than listening to the bus at large, because the keyboard is on that same bus for the
       * whole of a camera sitting (CAM-09) and a keyboard answer counted as a movement the camera
       * read would flatter the measurement precisely where honesty matters.
       *
       * In a cooperative sitting this is **one figure for the room, never one per child**
       * (D-076(6)): a per-child input-quality reading is a de facto sibling comparison. What stays
       * per child is `recordInputUncertainty`, which goes to the runtime's own analytics writer
       * exactly as it does for one body, because that is evidence integrity rather than a
       * comparison.
       */
      const countedBus = observeCameraActions(
        bus,
        (type) => {
          noteCameraActivity((tally) => withRecognisedAction(tally, type));
        },
        attemptWindow,
      );
      const noteUncertaintyCleared = (): void => {
        noteCameraActivity(withUncertaintyCleared);
      };
      const noteUncertaintyObserved = (): void => {
        if (isAttemptWindow(attemptWindow())) noteCameraActivity(withUncertaintyEpisode);
      };

      /**
       * Two heroes, one camera, the mission runtime none the wiser (Slice D′, CAM-07).
       *
       * The Slice B′ pair: one lane resolver — the only thing in the app entitled to decide whose
       * body a reading is — and two ordinary Phase 3 camera adapters, one per hero, each handed a
       * frame containing that hero's body and nothing else. Every Phase 3 invariant therefore
       * applies per lane because it is literally the same code reading the same shape.
       *
       * `requestMaxPoses(2)` is the Slice A′ seam, reaching a gameplay path for the first time. A
       * single-player sitting never asks, and keeps the `numPoses: 1` engine Phase 3 shipped along
       * with none of D-077(1)'s 1.9× per-frame cost.
       *
       * The recovery ladder is here too since Slice E′, and it is the **same** ladder: two shipped
       * single-body monitors, one per lane, composed by the two D-076(2) rulings — one child's loss
       * is a soft invitation that holds nothing, both children's loss is the whole-round pause.
       */
      const wireCoopCamera = (
        left: CalibrationProfile | null,
        right: CalibrationProfile | null,
      ): void => {
        releaseCameraChain();
        /**
         * Two bodies are asked for even in a **mixed** sitting (Slice F′).
         *
         * The hero on the keys is still standing in the room. A one-pose engine would hand the
         * resolver a single reading with nothing to separate it from, and the sibling's body could
         * then be attributed to the camera child — the duplicate-reading hazard D-077(2)(b)
         * measured. The 1.9× per-frame cost is the price of the second lane existing at all, and a
         * single-player sitting still never pays it.
         */
        camera.requestMaxPoses(2);

        const pair = createCooperativeCameraAdapter({
          bus: countedBus,
          tracker: camera.poseFrames,
          analytics: runtime.sceneServices.analytics,
          tunables: tunables.get(),
          // `null` rather than absent for a hero playing on the keyboard: no adapter is built for
          // them, so nothing reads their body, and nothing is ever recorded about a child the
          // camera is not watching (D-076(5)).
          left:
            left === null ? null : { calibration: poseCalibrationFrom(left.baselineMeasurements) },
          right:
            right === null
              ? null
              : { calibration: poseCalibrationFrom(right.baselineMeasurements) },
          // Both lanes join a mission already under way when there is one (S5-2, per lane).
          joiningMissionInProgress: missionUnderWay,
          /**
           * Which run of frames this is (S4-1).
           *
           * The camera effect below stops and restarts the stream whenever a mid-mission hero
           * training opens or closes, while this pair stays subscribed — `poseFrames` survives it on
           * purpose. A warm restart returns its first frame quickly enough that the resolver's
           * timestamp rule can miss the boundary, and the child in front of a restarted camera is
           * not necessarily the child who was there before it stopped.
           */
          streamGeneration: () => camera.streamGeneration,
          onUncertaintyCleared: noteUncertaintyCleared,
          onUncertaintyObserved: noteUncertaintyObserved,
          // The pair hands this to each lane adapter, so "the camera read a body" has one
          // definition for one child and for two (S3-6).
          onBodyAttributed: noteCameraBodySeen,
        });

        /**
         * The cooperative recovery watch (Slice E′, D-076(2), GDS §36).
         *
         * Built **before** the pair attaches, exactly as the single-player monitor is: on any given
         * frame it must see the situation before the detectors read it, so a frame that brings a
         * child back resumes the mission before anything that same frame detects could be discarded
         * as an answer given while the mission was held.
         *
         * What reaches this stage from it is the ordinary pause and one soft list. `trackingLost` is
         * both lanes and only both lanes, which is the whole of D-076(2)(a)/(b) — and it is also why
         * "a grown-up cannot resume into a camera that cannot see the child" becomes "…until at
         * least one hero is visible" without a line of new pause logic: the camera's own hold lifts
         * the moment either lane can be read.
         */
        let lastCoopRecovery: TrackingRecoveryState = CAMERA_HEALTHY;
        coopRecoveryMonitor = createCooperativeTrackingRecoveryMonitor({
          bus,
          pair,
          frames: camera.poseFrames,
          health: () => camera.health,
          starvation: camera.frameStarvation,
          tunables: tunables.get(),
          onChange: (next) => {
            if (cancelled) return;
            /**
             * Counted from the **sitting** and never from a lane (CAM-08, D-076(6)).
             *
             * The figure means "times the mission had to wait"; a soft state holds nothing, so
             * counting one would inflate a number whose whole meaning is interruption. And a
             * per-child count is the sibling comparison that ruling forbids — there is none here to
             * render, by construction.
             */
            const started = interruptionsBetween(lastCoopRecovery, next.sitting);
            lastCoopRecovery = next.sitting;
            for (let index = 0; index < started.lossEpisodes; index += 1) {
              noteCameraActivity(withLossEpisode);
            }
            for (let index = 0; index < started.fallbackOffers; index += 1) {
              noteCameraActivity(withFallbackOffer);
            }

            setRecovery(next.sitting);
            setRecoveryReason(next.sittingReason);
            setLaneRecovery(next.lanes);
            setPauseCause('camera', next.sitting.trackingLost);
          },
        });

        // Frames stopping altogether reaches both lanes (D-068(2)): a child whose camera is
        // unplugged mid-crouch must not keep a posture latched, whichever lane they are standing in.
        releaseStarvationWatch = camera.frameStarvation.subscribe((state) => {
          if (state.starved) pair.reportFramesStopped();
        });

        pair.setInputContext(inputContextNow);
        pair.attach();
        coopCamera = pair;
        // The same seat primitive the solo path goes through (S2-6 family): the lanes this pair was
        // actually built with, stated by the thing that built them. Idempotent, so the boot call
        // that precedes it costs nothing — what it buys is that "a camera was wired" and "the
        // sitting recorded who is on it" cannot come apart here either.
        if (!cancelled) {
          noteCameraLaneHeroes([
            ...(left === null ? [] : [HERO_1_PLAYER_ID]),
            ...(right === null ? [] : [HERO_2_PLAYER_ID]),
          ]);
        }
        // The one identity owner, lent to whatever mid-mission training a grown-up opens (S3).
        setRetrainingLaneView(() => () => pair.retrainingLaneView());
        neutralizeCameraRef.current = () => {
          pair.neutralize();
        };

        if (!cancelled) {
          setRecovery(CAMERA_HEALTHY);
          setRecoveryReason(null);
          setLaneRecovery([]);
          // Cooperative camera play is Full-Motion only, and its counting move is the jump itself.
          setPlaySpace({ mode: 'full-motion', substitute: null });
        }
      };

      const wireCamera = (profile: CalibrationProfile | null): void => {
        releaseCameraChain();

        /**
         * Both directions go through the one seat primitive (S2-6).
         *
         * This function is every single-player camera attach and detach there is — the boot wiring,
         * the `CAPABILITY_MISSING` sitting that wins a camera back mid-mission, and the refused
         * re-training that gives one up — and only the boot path used to record its seats. So a
         * recovered sitting could finish having reported nothing about the camera it had just used,
         * and a refused one went on naming the child as answering with their body for the rest of a
         * sitting whose camera had stopped. Idempotent, so the boot call that precedes this one
         * costs nothing.
         */
        if (!cancelled) {
          noteCameraLaneHeroes(profile === null ? [] : [players[0]?.playerId ?? HERO_1_PLAYER_ID]);
        }

        if (profile === null) {
          if (!cancelled) setPlaySpace(null);
          return;
        }

        // The CAM-08 taps and the attempt window are the sitting's, defined once above so that one
        // child and two are measured by the same rule (Slice H, hoisted by Slice D′).
        const adapter = createCameraInputAdapter({
          bus: countedBus,
          playerId: players[0]?.playerId ?? HERO_1_PLAYER_ID,
          tracker: camera.poseFrames,
          analytics: runtime.sceneServices.analytics,
          tunables: tunables.get(),
          calibration: poseCalibrationFrom(profile.baselineMeasurements),
          /**
           * Where the play space actually changes anything (D-059, GDS §15).
           *
           * One option, one detector swap, and nothing else in this file knows: the mission, its
           * content, the matchers and the scoring all see the same count onsets whichever part of
           * the body produced them. "The learning mechanic remains counting."
           */
          cameraMode,
          jumpSubstitute: substituteNow,
          // A camera that starts watching mid-mission has never seen this body move (S5-2).
          joiningMissionInProgress: missionUnderWay,
          /**
           * The closing bracket of an unreadable attempt (S2-6). Without it an attempt stayed open
           * until the camera read a deliberate action, so two genuinely separate attempts either
           * side of a confident, still moment counted as one.
           */
          onUncertaintyCleared: noteUncertaintyCleared,
          /**
           * The CAM-08 denominator, taken from the **raw** signal (S3-3).
           *
           * Not from `recordInputUncertainty`, which is deduplicated per capability per episode for
           * the session record's sake (D-062(4)). The two have different lifetimes: an unreadable
           * attempt that opens while the mission is held is correctly counted as nothing, and riding
           * on the deduplicated call then swallowed the child's *repeat* after Resume — the very
           * attempt this measurement is about. The session writer above is untouched and still
           * hears everything, ungated (D-070(1)).
           */
          onUncertaintyObserved: noteUncertaintyObserved,
          // This sitting's camera actually read a body (S3-6). At the attribution boundary, so a
          // body the camera saw and could not read still makes the summary's camera block exist.
          onBodyAttributed: noteCameraBodySeen,
        });

        /**
         * The camera's health, watched for the life of the mission (CAM-05, CAM-06, GDS §36).
         *
         * It decides nothing about the mission — it reports, and the pause it asks for is the
         * ordinary one. The mission runtime is never told there was a camera at all.
         */
        /**
         * What this monitor last said, for the interruption count.
         *
         * Per-monitor rather than per-sitting deliberately: a monitor rebuilt mid-mission (a
         * grown-up's `RESTART_CALIBRATION`) starts from calm, and comparing its first announcement
         * against the *old* monitor's last one would either invent an interruption or swallow one.
         */
        let lastRecovery: TrackingRecoveryState = CAMERA_HEALTHY;

        recoveryMonitor = createTrackingRecoveryMonitor({
          bus,
          playerId: adapter.playerId,
          frames: camera.poseFrames,
          health: () => camera.health,
          /**
           * The two things frames alone cannot report (Slice G, D-065(4)): a camera that has
           * stopped producing them at all, and a room nobody ever walked into. Presence is read
           * from the camera adapter rather than the bus, because the keyboard adapter announces
           * this same hero on attach (CAM-09) and would make every room look occupied.
           */
          starvation: camera.frameStarvation,
          presence: () => adapter.present,
          tunables: tunables.get(),
          onChange: (next, detail) => {
            if (cancelled) return;
            // Counted before anything is rendered, and counted only where an episode *begins*:
            // a mission held for four seconds is one wait, not a hundred frames of waiting.
            const started = interruptionsBetween(lastRecovery, next);
            lastRecovery = next;
            for (let index = 0; index < started.lossEpisodes; index += 1) {
              noteCameraActivity(withLossEpisode);
            }
            for (let index = 0; index < started.fallbackOffers; index += 1) {
              noteCameraActivity(withFallbackOffer);
            }

            setRecovery(next);
            setRecoveryReason(detail.reason);
            setPauseCause('camera', next.trackingLost);
          },
        });

        /**
         * The frames stopping altogether, reaching the adapter (D-068(2), starvation row).
         *
         * The starvation watchdog is the one wall clock in the machinery, and it lives outside the
         * frame stream by necessity — so nothing the detectors read can notice it. Until this
         * existed, a child whose camera was unplugged mid-crouch kept a held posture latched and was
         * never reported as having left the frame at all. Subscribed *after* the monitor, so the
         * mission is already held by the time the retractions are emitted.
         */
        releaseStarvationWatch = camera.frameStarvation.subscribe((state) => {
          if (state.starved) adapter.reportFramesStopped();
        });

        adapter.setInputContext(inputContextNow);
        adapter.attach();
        cameraInput = adapter;
        neutralizeCameraRef.current = () => {
          adapter.neutralize();
        };

        if (!cancelled) {
          setRecovery(CAMERA_HEALTHY);
          setRecoveryReason(null);
          // A single-player sitting has no lanes to invite anybody back into.
          setLaneRecovery([]);
          setPlaySpace({ mode: adapter.cameraMode, substitute: adapter.jumpSubstitute });
        }
      };

      if (laneProfiles === null) wireCamera(calibration);
      else if (anyLaneOnCamera) wireCoopCamera(laneProfiles.left, laneProfiles.right);

      /**
       * One hero stops answering with their body — and only that hero (Slice F′, D-076(5)).
       *
       * Surgical rather than a rebuild, deliberately: rebuilding the pair would re-baseline the
       * *other* hero mid-round (S5-2) and clear their latched zone, which is a child slowed down by
       * their sibling's camera trouble. Returns the heroes still on the camera so the caller can
       * tell "one lane went" from "this sitting has no camera left".
       *
       * D-076(5)'s explicit warning is honoured here and nowhere else is needed: there is no path
       * in this file that tears the whole camera down as a shortcut for one child's fallback.
       */
      releaseLaneRef.current = (playerId: PlayerId): readonly string[] => {
        if (coopCamera === null) return [];
        /**
         * The monitor first, and the order is the whole of it (S2-4).
         *
         * Detaching a lane's adapter *synchronously* emits `PLAYER_LEFT_FRAME` for that hero, which
         * is a departure the recovery monitor believes immediately — rightly, since a child cannot
         * leave a picture they were never in. So with the adapter released first, a lane the monitor
         * still had on its books went lost for the instant before it was removed, and a sitting whose
         * *other* hero was already out of shot was therefore held and released again inside one
         * synchronous call: a pause and a resume a family can see flicker, and a CAM-08 interruption
         * recorded against a camera that interrupted nothing.
         *
         * Removing the lane from the monitor first makes the release one act rather than two: from
         * the monitor's point of view this seat simply stops existing, and the departure that follows
         * is announced about a lane nothing is watching any more.
         */
        coopRecoveryMonitor?.releaseLane(playerId);
        coopCamera.releaseLane(playerId);
        const remaining = [...coopCamera.cameraPlayerIds];
        // The last lane going is the end of the camera half of the sitting, and the ordinary
        // teardown is what stops the tracks (PRV-02) once the stage sets the input mode.
        if (remaining.length === 0) releaseCameraChain();
        return remaining;
      };
      disposers.push(() => {
        releaseLaneRef.current = null;
      });

      /**
       * Disposed before the adapters, deliberately: a detaching keyboard or camera adapter emits
       * `PLAYER_LEFT_FRAME` for this same hero, and a monitor still listening would read the end
       * of the sitting as a child who had wandered off.
       */
      disposers.push(() => {
        releaseCameraChain();
      });

      // How a grown-up turns the camera off mid-mission (GDS §36's last rung). Held in a ref
      // because the control that calls it lives outside this effect.
      releaseCameraRef.current = () => {
        releaseCameraChain();
      };
      disposers.push(() => {
        releaseCameraRef.current = null;
      });

      /**
       * The two camera-era parent overrides, reaching the input chain (GDS §32, Slice G).
       *
       * Both are applied by the *stage*, on Resume, and neither is a mission event: the runtime is
       * not told, because nothing about the mission changed. What changed is which numbers the
       * detectors read and which movement fills the counting slot.
       */
      revalidateCameraRef.current = (): readonly CalibrationReuseRefusal[] => {
        if (cameraInput === null) return [];
        const resolution = resolveCalibration();
        if (resolution === null || resolution.profile !== null) return [];
        return resolution.refusals.filter(
          (refusal: CalibrationReuseRefusal) => refusal !== 'NO_PROFILE',
        );
      };
      disposers.push(() => {
        revalidateCameraRef.current = null;
      });

      /**
       * The same question, per live camera lane (Slice F′, retiring D-081(7)'s coop skip).
       *
       * Computed per lane and *acted on* per lane; never rendered per lane. The refusal notice this
       * feeds is the shipped sitting-wide one, which carries codes and names nobody — so the
       * D-076(6) ban on per-child input-quality surfaces holds while the evidence stays honest for
       * each child, which is exactly the split that ruling draws.
       */
      revalidateLanesRef.current = () => {
        if (coopCamera === null) return [];
        return coopCamera.cameraPlayerIds
          .map((playerId) => {
            const resolution = resolveLane(playerId);
            if (resolution === null || resolution.profile !== null) {
              return { playerId, refusals: [] as readonly CalibrationReuseRefusal[] };
            }
            return {
              playerId,
              refusals: resolution.refusals.filter(
                (refusal: CalibrationReuseRefusal) => refusal !== 'NO_PROFILE',
              ),
            };
          })
          .filter((lane) => lane.refusals.length > 0);
      };
      disposers.push(() => {
        revalidateLanesRef.current = null;
      });

      cameraOverridesRef.current = {
        applyCalibration(playerId?: string): CameraOverrideOutcome {
          /**
           * A cooperative retraining is applied to **one lane** (D-076(8), checklist row 15).
           *
           * `pair.reconfigure` is the Slice D′ seam that already scopes to one side and moves that
           * side's resolver calibration only, so the hero the grown-up did not pick is untouched in
           * every sense — their thresholds, their portals, their lane, their profile.
           */
          if (coopCamera !== null && playerId !== undefined) {
            const resolution = resolveLane(playerId);
            const laneProfile = resolution?.profile ?? null;
            const laneRefusals = (resolution?.refusals ?? []).filter(
              (refusal: CalibrationReuseRefusal) => refusal !== 'NO_PROFILE',
            );
            if (laneProfile === null) {
              // A fresh training that still cannot serve this mission takes this hero — and only
              // this hero — to the keys, with the reason on screen (architecture §20, D-066(5)).
              releaseLaneRef.current?.(playerId);
              return { attached: false, refusals: laneRefusals };
            }
            coopCamera.reconfigure(playerId, {
              calibration: poseCalibrationFrom(laneProfile.baselineMeasurements),
            });
            return { attached: true, refusals: [] };
          }

          const resolution = resolveCalibration();
          const profile = resolution?.profile ?? null;
          const refusals = (resolution?.refusals ?? []).filter(
            (refusal: CalibrationReuseRefusal) => refusal !== 'NO_PROFILE',
          );

          if (profile === null) {
            // A fresh training that still cannot serve this mission leaves the sitting exactly
            // where it was: at the keys, with the reason on screen (architecture §20, D-066(5)).
            wireCamera(null);
            return { attached: false, refusals };
          }

          if (cameraInput !== null) {
            // Reconfigured rather than rebuilt: the recovery episode, the monitor's own clocks and
            // anything the round has already accumulated all survive a change of numbers.
            cameraInput.reconfigure({
              calibration: poseCalibrationFrom(profile.baselineMeasurements),
            });
          } else {
            wireCamera(profile);
          }
          return { attached: true, refusals: [] };
        },

        setJumpSubstitute(substitute: JumpSubstitute): void {
          substituteNow = substitute;
          cameraInput?.reconfigure({ jumpSubstitute: substitute });
          if (!cancelled && cameraInput !== null) {
            setPlaySpace({ mode: cameraInput.cameraMode, substitute: cameraInput.jumpSubstitute });
          }
        },
      };
      disposers.push(() => {
        cameraOverridesRef.current = null;
      });

      // Shell requests reach the runtime here — including the ones the R and P keys raise.
      const shellSubscriptions = [
        bridge.on('shell:pause-requested', () => {
          runtime.pause();
        }),
        bridge.on('shell:resume-requested', () => {
          runtime.resume();
        }),
        bridge.on('shell:repeat-narration-requested', () => {
          runtime.repeatInstruction();
        }),
        bridge.on('parent:override', (command) => {
          runtime.applyParentOverride(command);
        }),
      ];
      disposers.push(() => {
        for (const unsubscribe of shellSubscriptions) unsubscribe();
      });

      /**
       * The platform's half of the stage: theme loading, the resolution report, the input
       * readout, the shell handshake and the overlay layer every module scene is handed. It
       * draws nothing, so the module's own scenes are what a child actually sees.
       */
      const chrome = new StageChromeScene({
        gameId: gameModule.id,
        assetIds: selectStageAssetIds(gameModule),
        assets,
        input: reader,
        bridge,
      });

      const host = createPhaserHost({
        parent: container,
        scene: chrome,
        // In a camera sitting the stage is drawn *over* the child (MVP notes §16): an opaque
        // canvas would hide the very thing the mission is played on top of.
        transparent: cameraSitting,
      });
      disposers.push(() => {
        host.destroy();
      });

      /**
       * The module contract, genuinely exercised (module spec §9, recon §5 option (a)).
       *
       * Every field is the real thing: the runtime's own score and teaching controllers rather
       * than copies, the session the runtime is recording, and a presentation context carrying
       * the grown-up's calmer-motion choice and the platform's own overlay container.
       */
      const contextFor = (stage: MissionStageDefinition): GameSceneContext => ({
        session: {
          sessionId,
          gameId: gameModule.id,
          missionId: mission.id,
          playerMode: config.playerMode,
          inputMode: sessionInputMode,
          startedAt: resumeFrom?.startedAt ?? new Date().toISOString(),
        },
        players,
        mission,
        stage,
        events: relay,
        input: reader,
        narration: voice,
        teaching: runtime.sceneServices.teaching,
        scoring: runtime.sceneServices.scoring,
        assets,
        analytics: runtime.sceneServices.analytics,
        presentation: {
          mode: gameModule.launchRequirements.presentationMode,
          viewport: { width: host.game.scale.width, height: host.game.scale.height },
          // The platform owns this container; a module positions overlays in it and can never
          // reach past it to a camera frame (module spec §25).
          overlayLayer: chrome.overlayLayer() as GamePresentationContext['overlayLayer'],
          /**
           * The camera layer, constructed for the first time (PS3-5).
           *
           * It is an opaque handle by design (module spec §25): a module may place its overlays
           * against the mirrored feed and can never read a pixel of it. `active` is a *getter* on
           * the session's own stream rather than a boolean captured at scene start — a scene that
           * asks halfway through a mission gets the truth, not the situation at boot. Absent
           * entirely in a keyboard sitting, because an inactive camera layer is not a thing a
           * scene should have to reason about.
           */
          ...(cameraSitting
            ? {
                cameraLayer: {
                  mirrored: true,
                  viewport: { width: host.game.scale.width, height: host.game.scale.height },
                  get active(): boolean {
                    return camera.isStreamActive();
                  },
                },
              }
            : {}),
          // What a grown-up chose for this sitting (D-037(1)). The consumption end of the seam
          // Slice D opened: scenes calm or shorten their effects rather than the host doing it.
          reducedMotion: config.reducedMotion,
        },
      });

      director = createStageSceneDirector({
        game: host.game,
        stages: mission.stages,
        contextFor,
        // Never a blank canvas: a stage whose scene cannot be built says so and the mission
        // carries on underneath it.
        fallbackFor: (stage, cause, sceneKey) =>
          new StageFallbackScene({
            sceneKey,
            stageLabel: stage.label ?? stage.id,
            detail: cause instanceof Error ? cause.message : String(cause),
          }),
        onError: (message) => {
          if (!cancelled) setStageError(message);
        },
        // Keeps the overlay layer above the mechanic that was just started.
        afterStart: () => {
          host.game.scene.bringToTop(STAGE_CHROME_SCENE_KEY);
        },
      });
      disposers.push(() => {
        director?.dispose();
        director = null;
      });

      sessionControls.attach();
      // The keys never stop working in a camera sitting (CAM-09), and that structural fact *is* the
      // keyboard fallback GDS §35/§36 asks for. The camera half attached itself when it was wired.
      for (const keyboard of keyboards) keyboard.attach();

      // The stage may have unmounted while we were awaiting; tear down what we just built.
      if (cancelled) {
        runTeardown();
        return;
      }

      // The mission drives itself from here. A failure inside it tears the stage down through
      // the same path as any other boot failure rather than rejecting an ignored promise.
      await runtime.start(resumeFrom);
    })().catch(reportBootFailure);

    return () => {
      cancelled = true;
      abort.abort();
      runTeardown();
    };
  }, [
    activePlayer,
    audio,
    bridge,
    camera,
    // Stable (`useCallback` with no dependencies); listed so the seats fact has one owner.
    noteCameraLaneHeroes,
    noteCameraBodySeen,
    cameraInputEnabled,
    gameModule,
    narration,
    noteCameraActivity,
    repositories,
    // Derived from `repositories` and stable with it; listed so the projection has one owner.
    sittingRepositories,
    sessionLaunchConfig,
    setPauseCause,
    tunables,
  ]);

  /**
   * The camera itself, for as long as the stage is on screen (architecture §23, PRV-02).
   *
   * Separate from the boot effect because the video element only exists once the stage knows it is
   * a camera sitting, and because the obligation it carries is its own: **leaving the mission ends
   * the tracks.** The pose engine stays warm (D-060(3)) — that is the sitting's, not the screen's —
   * but the stream and the indicator go with the screen, on every exit path, including the abrupt
   * ones, because this is a React cleanup rather than a button handler.
   */
  useEffect(() => {
    /**
     * Hero training needs the camera even in a sitting that is not using it (Slice G).
     *
     * `CAPABILITY_MISSING` is exactly that sitting: the keys are carrying the mission because
     * architecture §20 refused the profile, and the only way out is to measure the child again —
     * which cannot happen with the camera off. It goes off again the moment training ends unless
     * the new profile earned the sitting a camera.
     */
    if (inputMode === 'KEYBOARD' && !calibrating) return;

    camera.attach(cameraVideoRef.current);
    void camera.start();

    return () => {
      camera.setSampleIntervalMs(null);
      camera.stop();
      camera.attach(null);
    };
  }, [camera, calibrating, inputMode]);

  /**
   * How often the camera is asked to look (CAM-06).
   *
   * Separate from the effect above so that degrading the cadence does not restart the camera — a
   * child would see the indicator blink and the feed drop out because their laptop got busy.
   *
   * Gameplay cadence, not the readiness screen's: the top of a five-year-old's jump is brief. When
   * the machine cannot keep up the session looks *less often* rather than dropping what it sees —
   * every frame taken is still read by every detector, and every answer still counts. That is the
   * whole difference between degrading honestly and silently losing input.
   */
  useEffect(() => {
    if (inputMode === 'KEYBOARD' && !calibrating) return;

    const { pose } = tunables.get();
    const fps = recovery.lowPerformance ? pose.lowPerformanceTargetFps : pose.poseTargetFps;
    camera.setSampleIntervalMs(Math.round(1_000 / fps));
  }, [calibrating, camera, inputMode, recovery.lowPerformance, tunables]);

  // The parent summary is built by the *game*, from the session the platform recorded. The shell
  // supplies the stored estimates and renders the result; it never scores or interprets anything.
  useEffect(() => {
    if (completion === null) return;

    let cancelled = false;
    void (async () => {
      try {
        /**
         * Read as the child, delivered as the seat (R2-3, D-052(3)).
         *
         * The child, not the seat, is what storage is keyed by: a guest has no stored history to
         * show, and must not be shown the history of whoever last sat in that lane. Handing the
         * game one flat list threw that attribution away again on the way out — the adapter could
         * then only match by skill id, so a sibling with no history was shown the other child's
         * estimate under their own name. Each seat gets its own list, and a seat whose child has
         * no history gets an empty one.
         */
        const seats = completion.session.record.players;
        const perSeat = await Promise.all(
          seats.map((player) => {
            const subjectId = learningSubjectIdOf(player);
            return subjectId === null
              ? Promise.resolve<SkillEstimate[]>([])
              : repositories.skills.getSkillEstimates(subjectId);
          }),
        );
        const estimates: Record<PlayerId, readonly SkillEstimate[]> = {};
        seats.forEach((player, index) => {
          estimates[player.playerId] = perSeat[index] ?? [];
        });

        const built = gameModule.parentSummaryAdapter.buildSummary(completion.session, estimates);
        /**
         * The sitting's own aggregate, attached where the sitting is (PS6-9).
         *
         * Written from here rather than from the mission runtime, which is frozen and which has no
         * business knowing what a grown-up pressed on the stage chrome. It is an **optional field
         * on the record that already exists** — no new `thrive.v1.*` key — so a build that never
         * writes it reads back exactly as before. Flagged for the storage review.
         *
         * **After the summary is built, deliberately.** Awaiting a storage write first put a whole
         * extra turn of the event loop between `data-mission-complete` and the finished session
         * reaching the game module — which is a window every test that reads one has to wait
         * across, and BL-007's own measurement caught it widening.
         */
        const used = parentOverridesRef.current;
        /**
         * Absent, never zero (P6R1-8 — the PS5-5 optional-field contract this field claims).
         *
         * The declaration says "absent means this build, or this sitting, did not count — never
         * zero", and round 1's writer then wrote `0` on every completion, which makes the contract
         * unreadable in both directions: a stored zero can no longer be told from a sitting nobody
         * instrumented, and the DTO's careful "absent stays absent" projection had nothing left to
         * protect. A count is written when there is a count.
         */
        await sittingRepositories().sessions.completeSession(completion.session.record.sessionId, {
          ...completion.session.summary,
          ...(used > 0 ? { parentOverridesUsed: used } : {}),
        });
        if (!cancelled) {
          /**
           * What the camera managed, frozen with the summary it belongs to (S7).
           *
           * A sitting that ever read a body reports what it read, whatever it ended on: the figures
           * describe what happened, and what happened does not stop having happened when a family
           * moves to the keys. A sitting that never read one says nothing about a camera at all.
           */
          const coverage = cameraCoverageRef.current;
          setSummaryCameraQuality(
            coverage.cameraSeen
              ? sittingCameraQuality(cameraActivityRef.current, coverageFromHistory(coverage))
              : null,
          );
          setParentOverridesUsed(used);
          setParentSummary(built);
        }
      } catch (cause) {
        // A summary that cannot be built must not swallow the fact that the mission finished.
        if (!cancelled) setStageError(cause instanceof Error ? cause.message : String(cause));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [completion, gameModule, repositories, sittingRepositories]);

  useEffect(
    () => () => {
      if (exitTimerRef.current !== null) clearTimeout(exitTimerRef.current);
    },
    [],
  );

  /**
   * GDS §36's last rung, taken (Slice E).
   *
   * The keyboard has been live all along — that is CAM-09, and it is why this is an *offer* rather
   * than a repair. What electing it does is stop pretending: the camera stops, the indicator goes
   * out, the child's body stops being watched, the camera's hold on the mission is released, and
   * the mission carries on from exactly where it paused. Nothing is scored, nothing is lost, and
   * the sitting's recorded input mode is untouched — the camera really was live for the part of
   * the session it was live for.
   */
  const electKeyboardFallback = (control?: HTMLButtonElement): void => {
    releaseCameraRef.current?.();
    releaseCameraRef.current = null;
    setRecovery(CAMERA_HEALTHY);
    setRecoveryReason(null);
    setLaneRecovery([]);
    // Nobody is on the camera now, and every surface built from that fact must say so (S4 family):
    // `data-camera-heroes` named both heroes for the rest of a sitting whose camera had stopped.
    noteCameraLaneHeroes([]);
    setCalibrating(false);
    setPendingCalibration(null);
    setTrainingHeroId(null);
    setPauseCause('camera', false);
    setCameraFallbackElected(true);
    // Which tears the camera down through the ordinary lifetime effect: tracks stopped, indicator
    // out, feed gone (PRV-02).
    setInputMode('KEYBOARD');
    control?.blur();
  };

  /**
   * One hero carries on with their keys, and nobody else's sitting changes (Slice F′, D-076(5)).
   *
   * The per-child counterpart of `electKeyboardFallback`, and the reason D-076(5) warned against a
   * sitting-wide teardown: taking the camera away from a child who is happily playing because their
   * sibling stepped out of shot is not a recovery. Their keys have been live all along (CAM-09), so
   * this stops pretending rather than repairing anything.
   *
   * When the **last** camera lane goes the sitting genuinely has no camera left, and it takes the
   * ordinary keyboard path — which is what stops the tracks and puts the indicator out (PRV-02).
   */
  const electLaneKeyboard = (playerId: string, control?: HTMLButtonElement): void => {
    const remaining = releaseLaneRef.current?.(playerId) ?? [];
    noteCameraLaneHeroes(remaining);
    setLaneRecovery((lanes) => lanes.filter((lane) => lane.playerId !== playerId));
    setKeyboardElectedHeroes((heroes) =>
      heroes.includes(playerId) ? heroes : [...heroes, playerId],
    );
    setRetrainingPlayerId((chosen) => (chosen === playerId ? null : chosen));

    if (remaining.length === 0) {
      setRecovery(CAMERA_HEALTHY);
      setRecoveryReason(null);
      setLaneRecovery([]);
      setPauseCause('camera', false);
      setInputMode('KEYBOARD');
    }
    control?.blur();
  };

  /**
   * The grown-up's Resume, with whatever they changed while the mission was held (D-059 PS3-2).
   *
   * The order is the whole of the contract. Hero training's measurements are applied *before* the
   * hold is lifted, so the first frame the mission is running for is already read against the new
   * numbers — and never during a round, because the mission was paused the entire time the parent
   * was choosing. If the fresh profile still cannot serve this mission the sitting carries on at the
   * keys and is told why (architecture §20, D-066(5)); it is never left stranded.
   */
  const applyPendingCameraChanges = (): void => {
    setCalibrating(false);
    setTrainingHeroId(null);
    if (pendingCalibration === null) return;
    setPendingCalibration(null);

    /**
     * In a cooperative sitting the retraining belongs to the hero it **measured** (Slice F′; S6).
     *
     * Named when the training completed rather than read from the chooser now, because the chooser
     * is a live control and the measurements are a finished fact about one child. Everything below
     * then stays scoped to that lane: a refused re-training takes *that hero* to the keys and leaves
     * their sibling's camera exactly where it was, which is the whole of D-076(8)'s
     * no-cross-contamination rule applied to the recovery path rather than only to the measurements.
     */
    const target = cooperativeCameraSitting ? pendingCalibration.playerId : null;
    const outcome =
      (target === null
        ? cameraOverridesRef.current?.applyCalibration()
        : cameraOverridesRef.current?.applyCalibration(target)) ?? null;
    if (outcome === null) return;

    setCameraRefusals(outcome.attached ? [] : outcome.refusals);
    setCalibrationNote(
      outcome.attached ? null : INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationRefused,
    );

    if (target !== null) {
      // The sitting keeps its camera for as long as any lane still has one; only the hero who was
      // refused moves to the keys, through the one per-child primitive.
      if (!outcome.attached) electLaneKeyboard(target);
      return;
    }

    if (outcome.attached) {
      /**
       * A sitting that was refused the camera at launch now has one (D-066(5)).
       *
       * The *recorded* `SessionInputMode` is untouched, and deliberately so: the runtime was given
       * it when the mission started and this file does not reach into `src/platform/missions`. The
       * record therefore describes how the sitting began. Recorded as a bound rather than papered
       * over — the honest mirror of D-065(2)'s "the record describes the sitting".
       */
      setInputMode('MIXED');
      setCameraFallbackElected(false);
      return;
    }

    /**
     * A refused re-training tears the camera down — completely (S1-6, D-067(1)'s F2 ruling).
     *
     * `wireCamera(null)` has already detached the semantic adapter, but detaching the adapter is
     * not turning the camera off: the sitting stayed `MIXED`, so the lifetime effect kept the
     * stream, the mirrored feed, the camera-active indicator and the pose loop running while the
     * notice on screen said the keys were carrying the mission. One profile per child means the old
     * measurements are already gone, so there is nothing for a live camera to read a body against.
     * Setting the mode is what takes the ordinary keyboard-fallback path: tracks stopped, indicator
     * out, feed gone (PRV-02).
     *
     * `cameraFallbackElected` is deliberately NOT set: the grown-up did not elect the keyboard, and
     * leaving it false keeps *Restart calibration* on the pause panel so a second attempt at hero
     * training can still win the camera back (D-066(5)).
     */
    setInputMode('KEYBOARD');
    // The camera's own hold cannot clear itself once its monitor is gone.
    setPauseCause('camera', false);
  };

  /**
   * Architecture §20's two live recalibration triggers, finally connected (S1-7).
   *
   * Both ask the same question — *can this profile still serve the child in front of this camera?* —
   * and both give the same answer when it is no: the camera stops being read, the keys carry the
   * mission, and the grown-up is told why, with *Restart calibration* still on the pause panel. That
   * is the D-066(5) path this file already ships; what is new is that anything can reach it while a
   * mission is running.
   *
   *  - **The camera changed.** Re-asked the moment the mission's stream is live, because the device
   *    id does not exist before then.
   *  - **Confidence fell.** `hasTrackingQualityFallen` against the live reading — but only once it
   *    has *lasted*, over `trackingLossPauseMs` worth of readings. A single dim frame is not a
   *    recalibration prompt; interrupting a five-year-old for one is how a session ends.
   */
  useEffect(() => {
    if (inputMode === 'KEYBOARD' || cameraFallbackElected || missionEnded) return;
    const { pose } = tunables.get();
    const sustainedReadings = Math.max(
      1,
      Math.round((pose.trackingLossPauseMs / 1_000) * pose.poseTargetFps),
    );
    let fallenFor = 0;
    let handled = false;

    const requestRecalibration = (refusals: readonly CalibrationReuseRefusal[]): void => {
      if (handled || refusals.length === 0) return;
      handled = true;
      releaseCameraRef.current?.();
      releaseCameraRef.current = null;
      setRecovery(CAMERA_HEALTHY);
      setRecoveryReason(null);
      setLaneRecovery([]);
      setPauseCause('camera', false);
      setCameraRefusals(refusals);
      // The camera is off for this sitting; nothing may keep naming heroes as being on it (S4 family).
      noteCameraLaneHeroes([]);
      setInputMode('KEYBOARD');
    };

    /**
     * The cooperative shape of the same two triggers (Slice F′, retiring D-081(7)'s skip).
     *
     * Every lane the judgment covers is taken to the keys through the **per-child** primitive, one
     * call per hero. That is not the sitting-wide teardown D-076(5) warns about: the shortcut it
     * forbids is releasing lanes the judgment did not cover, and no branch here does that.
     */
    const releaseLanes = (
      lanes: readonly string[],
      refusals: readonly CalibrationReuseRefusal[],
    ): void => {
      if (handled || lanes.length === 0 || refusals.length === 0) return;
      handled = true;
      setCameraRefusals(refusals);
      for (const playerId of lanes) electLaneKeyboard(playerId);
    };

    const check = (): void => {
      if (handled) return;
      const snapshot = camera.getSnapshot();
      if (!snapshot.streamActive) return;

      if (cooperativeCameraSitting) {
        // ── The camera itself, asked per live lane and answered per live lane.
        const refusedLanes = revalidateLanesRef.current?.() ?? [];
        if (refusedLanes.length > 0) {
          releaseLanes(
            refusedLanes.map((lane) => lane.playerId),
            [...new Set(refusedLanes.flatMap((lane) => lane.refusals))],
          );
          return;
        }

        /**
         * ── Confidence fell, judged sitting-neutrally.
         *
         * There is exactly one confidence figure at this seam — the strongest body the camera can
         * see — so a "per-lane" reading here would be fiction, and inventing one would be the
         * per-child input-quality surface D-076(6) forbids in all but name. What is honest is to
         * trip only when the reading has fallen below what **every** live camera lane's profile
         * expects. `every` rather than `some` fails safe toward silence: one child's stricter
         * profile can never interrupt a sitting, and nothing per-child is computed, kept or shown.
         */
        const profiles = cameraLaneHeroes
          .map((playerId) => camera.calibration.get(playerId))
          .filter((profile): profile is CalibrationProfile => profile !== null);
        const observedNow = snapshot.readings.body?.coreConfidence ?? null;
        if (profiles.length === 0 || observedNow === null) {
          fallenFor = 0;
          return;
        }
        fallenFor = profiles.every((profile) => hasTrackingQualityFallen(profile, observedNow))
          ? fallenFor + 1
          : 0;
        if (fallenFor >= sustainedReadings) {
          releaseLanes([...cameraLaneHeroes], ['QUALITY_TOO_LOW']);
        }
        return;
      }

      // The camera itself, asked once the stream can answer.
      requestRecalibration(revalidateCameraRef.current?.() ?? []);
      if (handled) return;

      const profile = camera.calibration.get(cameraSetup?.playerId ?? HERO_1_PLAYER_ID);
      const observed = snapshot.readings.body?.coreConfidence ?? null;
      if (profile === null || observed === null) {
        fallenFor = 0;
        return;
      }
      fallenFor = hasTrackingQualityFallen(profile, observed) ? fallenFor + 1 : 0;
      if (fallenFor >= sustainedReadings) requestRecalibration(['QUALITY_TOO_LOW']);
    };

    check();
    return camera.subscribe(check);
    // `electLaneKeyboard` is deliberately not a dependency: it is re-created on every render and
    // reacting to that would re-subscribe the watch — and reset its sustain counter — on every
    // frame that moves any state, which is exactly what "only once it has lasted" forbids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    camera,
    cameraFallbackElected,
    cameraLaneHeroes,
    cameraSetup,
    cooperativeCameraSitting,
    inputMode,
    missionEnded,
    setPauseCause,
    tunables,
  ]);

  const requestExit = (): void => {
    bridge.emit('shell:exit-requested', { reason: 'player-exit' });
    exitTimerRef.current = setTimeout(leave, EXIT_FALLBACK_MS);
  };

  /**
   * The parent summary is a state flip inside this route, not a navigation.
   *
   * The finished session lives in React state and is not persisted, so navigating away from it
   * would lose it. The stage is hidden rather than unmounted for the same reason the mission
   * runtime owns its own teardown: the Phaser host is still attached to that container until the
   * stage itself goes.
   */
  const summaryOnScreen = parentSummary !== null && summaryRequested;
  const hiddenWhileSummary = summaryOnScreen ? { display: 'none' } : undefined;
  /**
   * Which seats the sitting-wide camera figures cover (Slice G′, D-076(6); S7).
   *
   * Seats, never quality: `cameraLaneHeroes` is the shipped `data-camera-heroes` state (D-083(1)) and
   * `heroLabels` is the roster this stage built. In a mixed sitting one child is answering with the
   * keys, contributes nothing to a camera figure, and a grown-up reading the figure as covering both
   * children would be misled by a number that is perfectly accurate.
   *
   * The panel is a live readout and this is its live answer; the *summary* reads the sitting's own
   * history instead (S7), and the two cannot contradict each other because lanes only ever shrink.
   */
  const cameraCoverage = cameraQualityCoverage(
    Object.keys(heroLabels).length,
    cameraLaneHeroes.length,
  );
  const endedEarly = completion?.endedEarly === true;
  /**
   * The camera surfaces, and the one rule they all share: nothing about a camera problem is on
   * screen once the mission is over or the grown-up has turned the camera off. A finished mission
   * is not waiting for anybody, and an elected keyboard sitting has no camera to complain about.
   */
  const cameraLive = inputMode !== 'KEYBOARD' && !cameraFallbackElected;
  const showRecoveryOverlay = cameraLive && recovery.trackingLost && !missionEnded;
  /**
   * Which invitation this pause deserves (Slice G for one child, Slice E′ for two).
   *
   * Chosen next to the copy rather than here, because the choice *is* copy: which of GDS §36's
   * situations a family is actually in decides which sentence is true for them, and only two of
   * them change when there are two children in the room.
   */
  const recoveryInvitation = recoveryInvitationFor(recoveryReason, {
    cooperative: cooperativeCameraSitting,
  });
  /**
   * The running game's own art, for the shell's own panels (Slice E″).
   *
   * A second resolver rather than a share of the boot effect's: this one is needed during *render*
   * and the stage's is built inside an async boot that may not have run yet. Building one is
   * cheap — it indexes the manifest and holds no resources — and it means a panel can never be
   * caught rendering against a half-initialised stage.
   */
  const stageArt = useMemo(
    () => createThemeAssetResolver(gameModule.themeManifest),
    [gameModule.themeManifest],
  );
  /**
   * The soft state: one hero the camera cannot read while the mission carries on (D-076(2)(a)).
   *
   * Never rendered while the sitting itself is held — a child reading "step back in" beside an
   * overlay saying the same thing to both of them would be the game talking over itself — and never
   * in a single-player sitting, which has no lane to be missing from.
   */
  const laneInvitations =
    !cameraLive || missionEnded || recovery.trackingLost
      ? []
      : laneRecovery.filter((lane) => lane.lost);
  /**
   * Who a mid-mission hero training could measure (Slice F′, D-076(8), retiring D-081(8)).
   *
   * In a cooperative sitting: the heroes actually on the camera, and only them — a hero playing on
   * keys is not offered a control that would do nothing (the Phase 1 no-dead-buttons precedent),
   * and bringing a keys hero *onto* the camera mid-sitting is a different flow this slice
   * deliberately does not build. In a single-player sitting: exactly what shipped, including the
   * sitting architecture §20 refused, which is the one D-066(5) exists to recover.
   */
  const retrainableHeroes: readonly { playerId: string; mode: CameraMode }[] =
    cooperativeCameraSitting
      ? cameraLaneHeroes.map((playerId) => ({ playerId, mode: 'full-motion' as CameraMode }))
      : cameraSetup === null
        ? []
        : [cameraSetup];
  const trainingTarget =
    retrainableHeroes.find((hero) => hero.playerId === retrainingPlayerId) ??
    retrainableHeroes[0] ??
    null;
  /**
   * The hero the trainer on screen belongs to (S6).
   *
   * `trainingTarget` is what the chooser is *showing*; this is what a training that has already
   * started is *about*, and the two are the same thing right up until a grown-up's hand brushes the
   * chooser mid-training. Once a training opens, its hero is fixed for its whole life — the seven
   * steps, the wait for Resume, and the application — so nothing a control does afterwards can send
   * one child's measurements to their sibling.
   */
  const trainerHero =
    trainingHeroId === null
      ? trainingTarget
      : (retrainableHeroes.find((hero) => hero.playerId === trainingHeroId) ?? null);
  /**
   * Which trainer a mid-mission hero training mounts, and what it reads identity from (S3).
   *
   * `null` means "no trainer may be mounted at all": a cooperative sitting whose pair has gone has
   * nothing entitled to say whose body is in the picture, and the honest answer to that is to offer
   * no training rather than to run one that guesses. A single-player sitting carries no lane view
   * and its construction is byte-identical to the shipped one.
   */
  const trainerIdentity:
    { cooperative: true; laneResolver: () => LaneResolver } | Record<string, never> | null =
    !cooperativeCameraSitting
      ? {}
      : retrainingLaneView === null
        ? null
        : { cooperative: true, laneResolver: retrainingLaneView };
  const showPerformanceNotice = cameraLive && recovery.lowPerformance && !missionEnded;
  const showFallbackOffer = cameraLive && recovery.fallbackOffered && !missionEnded;
  /** The mission ran to its end. An ended sitting is not one, and must never read as one. */
  const missionComplete = missionEnded && !endedEarly;

  /**
   * A grown-up's mid-mission control (game design spec §32).
   *
   * Sends the command and hands the keyboard straight back: the input adapter ignores keys typed
   * into a focused control, so a control that keeps focus leaves the child's keys dead until
   * someone uses a mouse — and keyboard-only play is the accessibility path (ACC-03, D-040(2)).
   */
  const runParentAction = (
    command: ParentOverrideCommand['type'],
    control: HTMLButtonElement,
  ): void => {
    noteParentOverride();
    bridge.emit('parent:override', { type: command, reason: 'parent-control' });
    control.blur();
  };

  return (
    <section className="stage">
      {!summaryOnScreen || parentSummary === null ? null : (
        <ParentSummaryView
          summary={parentSummary}
          {...(completion === null ? {} : { reward: completion.reward })}
          {...(parentOverridesUsed === null ? {} : { parentOverridesUsed })}
          {...(summaryCameraQuality === null
            ? // A sitting that never read a body has no camera to report on, and the summary reads
              // exactly as it did before this line existed.
              {}
            : { cameraQuality: summaryCameraQuality })}
          onPlayAgain={playAgain}
          onLeave={requestExit}
        />
      )}

      <div className="stage__chrome" style={hiddenWhileSummary}>
        <h1 style={{ marginRight: 'auto' }}>{gameModule.metadata.title}</h1>
        <button
          type="button"
          className="button button--quiet"
          data-testid="pause-button"
          onClick={(event) => {
            // Their own hold, and only theirs (Slice E): a Resume pressed while the camera still
            // cannot see the child lifts this and leaves the mission where it is, because a
            // mission resumed into a camera that cannot see is a mission the child cannot play.
            //
            // Anything the grown-up changed while the mission was held takes effect here, before
            // the hold is lifted (D-059 PS3-2): fresh measurements are what the detectors read from
            // the first frame the mission is running for.
            if (paused) applyPendingCameraChanges();
            setPauseCause('parent', !paused);
            // Hand the keyboard back to the game. The input adapter deliberately ignores keys
            // typed into a control — otherwise Space would re-trigger whatever button has focus —
            // so a grown-up who taps Resume would otherwise leave the child's keys dead until
            // somebody clicked elsewhere. Keyboard-only play is the accessibility path (ACC-03),
            // so it cannot depend on a mouse.
            event.currentTarget.blur();
          }}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
        <button
          type="button"
          className="button button--quiet"
          data-testid="exit-button"
          onClick={requestExit}
        >
          Leave game
        </button>
      </div>

      {!paused || missionEnded || summaryOnScreen ? null : (
        <div className="panel" data-testid="parent-actions">
          <h2 style={{ marginTop: 0 }}>Parent actions</h2>
          <p className="lede">
            For the grown-up. The mission stays paused until you press Resume, and nothing here
            marks anything wrong.
          </p>
          {PARENT_ACTIONS.map((action) => (
            <p key={action.testId}>
              <button
                type="button"
                className="button button--quiet"
                data-testid={action.testId}
                onClick={(event) => {
                  runParentAction(action.command, event.currentTarget);
                }}
              >
                {action.label}
              </button>{' '}
              <span className="lede">{action.description}</span>
            </p>
          ))}

          {/*
           * The camera-era half of GDS §32's register (Slice G, D-041(1)).
           *
           * Offered only to a sitting that asked for the camera — a family playing at the keys is
           * not shown controls for a camera they never turned on, exactly as Phase 1 declined to
           * show these as dead buttons. Neither of them is a mission command: they change what the
           * detectors read, and the runtime is never told either happened.
           */}
          {retrainableHeroes.length === 0 || cameraFallbackElected ? null : (
            <>
              {/*
               * Which hero is retrained (Slice F′, D-076(8), retiring D-081(8)'s "not offered").
               *
               * Only in a cooperative sitting: a single-player one has exactly one hero and a
               * chooser over one child would be a control that decides nothing. The list holds only
               * heroes already answering with their bodies, so nothing here can silently retrain a
               * child the camera is not watching — and nothing here is a comparison: it asks who is
               * ready, and says that the other hero's own moves are left exactly as they are
               * (turn-free by the E-R1 ruling, D-099(5); reworded in round 5).
               */}
              {!cooperativeCameraSitting ? null : (
                <p>
                  <label htmlFor="parent-calibration-hero">
                    {INVENTED_CAMERA_OVERRIDE_COPY.chooseHeroLabel}
                  </label>{' '}
                  <select
                    id="parent-calibration-hero"
                    data-testid="parent-calibration-hero"
                    value={trainerHero?.playerId ?? trainingTarget?.playerId ?? ''}
                    /* A training in progress owns its hero (S6): the chooser asks who to train, and
                       the question has been answered until this one is applied or abandoned. */
                    disabled={calibrating}
                    onChange={(event) => {
                      setRetrainingPlayerId(event.currentTarget.value);
                      event.currentTarget.blur();
                    }}
                  >
                    {retrainableHeroes.map((hero) => (
                      <option key={hero.playerId} value={hero.playerId}>
                        {heroLabels[hero.playerId] ?? hero.playerId}
                      </option>
                    ))}
                  </select>{' '}
                  <span className="lede">
                    {INVENTED_CAMERA_OVERRIDE_COPY.chooseHeroDescription}
                  </span>
                </p>
              )}
              <p>
                <button
                  type="button"
                  className="button button--quiet"
                  data-testid="parent-action-restart-calibration"
                  onClick={(event) => {
                    noteParentOverride();
                    setCalibrationNote(null);
                    /**
                     * The grown-up's own hold, taken before the trainer opens (S1-2, D-059 PS3-2).
                     *
                     * This panel is reachable from a pause the *camera* is holding — a child who
                     * wandered off — and that hold clears the moment they walk back in for
                     * training. Without a parent hold of its own the aggregate pause would empty,
                     * the mission would resume underneath the trainer, the overlay would unmount
                     * mid-training, and the child's training movements would answer a live round.
                     * PS3-2 is explicit that this is a pause-*and-resume* variant: only the
                     * grown-up's Resume lets the mission go.
                     */
                    setPauseCause('parent', true);
                    setTrainingHeroId(trainingTarget?.playerId ?? null);
                    setCalibrating(true);
                    event.currentTarget.blur();
                  }}
                >
                  {INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationLabel}
                </button>{' '}
                <span className="lede">
                  {INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationDescription}
                </span>
              </p>

              {playSpace === null || playSpace.substitute === null ? null : (
                <p>
                  <label htmlFor="parent-jump-substitute">
                    {INVENTED_CAMERA_OVERRIDE_COPY.substituteLabel}
                  </label>{' '}
                  <select
                    id="parent-jump-substitute"
                    data-testid="parent-jump-substitute"
                    value={playSpace.substitute}
                    onChange={(event) => {
                      const substitute = event.currentTarget.value as JumpSubstitute;
                      noteParentOverride();
                      cameraOverridesRef.current?.setJumpSubstitute(substitute);
                      // Kept on the config this run is using, so "play again" keeps the choice a
                      // grown-up made mid-mission (D-037(8)); the shell's own copy is deliberately
                      // not written back, because that is what restarts the stage.
                      launchConfigRef.current = {
                        ...launchConfigRef.current,
                        jumpSubstitute: substitute,
                      };
                      event.currentTarget.blur();
                    }}
                  >
                    {JUMP_SUBSTITUTES.map((substitute) => (
                      <option key={substitute} value={substitute}>
                        {JUMP_SUBSTITUTE_COPY[substitute]}
                      </option>
                    ))}
                  </select>{' '}
                  <span className="lede">
                    {INVENTED_CAMERA_OVERRIDE_COPY.substituteDescription}
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/*
       * Hero training, over a mission that stays exactly where it is (GDS §32, D-059 PS3-2).
       *
       * Mounted beside the parent actions rather than instead of the stage: the mission underneath
       * is paused, not gone, and the measurements it produces are applied by the grown-up's Resume.
       */}
      {!paused ||
      missionEnded ||
      summaryOnScreen ||
      !calibrating ||
      trainerHero === null ||
      trainerIdentity === null ? null : (
        <StageCalibrationOverlay
          camera={camera}
          /*
           * The theme's own body outline and its "training done" picture (brief §9, Slice E″).
           *
           * Handed in as resolved art rather than looked up inside the overlay, so the platform's
           * training screen still knows nothing about which game is running.
           */
          art={{ resolver: stageArt, roles: PLATFORM_ASSET_ROLES }}
          playerId={trainerHero.playerId}
          cameraMode={trainerHero.mode}
          // The cooperative trainer measures this hero's own half of the picture, tolerates the
          // sibling standing at the edge of theirs, and asks the sitting's ONE resolver whose body
          // it is looking at (Slice C′'s machinery, reused not re-derived; S3).
          {...trainerIdentity}
          onCaptured={() => {
            // The hero this training measured, recorded with the measurements (S6).
            setPendingCalibration({ playerId: trainerHero.playerId });
          }}
          onAbandon={() => {
            // The old profile stands and the mission is still held by the grown-up's own pause:
            // backing out of training must never be a way to strand a sitting.
            setCalibrating(false);
            setPendingCalibration(null);
            setTrainingHeroId(null);
          }}
        />
      )}

      {calibrationNote === null || missionEnded ? null : (
        <p className="notice" data-testid="stage-calibration-note">
          {calibrationNote}
        </p>
      )}

      {/*
       * The stage, and — in a camera sitting — the child underneath it (MVP notes §16).
       *
       * The feed is a sibling of the Phaser canvas rather than anything Phaser owns: the module
       * never gets a video element, only the opaque `cameraLayer` handle. Placeholder-grade
       * compositing (mirrored feed behind a transparent canvas); the visual polish of MVP notes
       * §16 — dimming during story beats, the player ring, the framing — is Phase 5's.
       */}
      <div className="stage__scene" style={hiddenWhileSummary}>
        {inputMode === 'KEYBOARD' && !calibrating ? null : (
          <video
            ref={cameraVideoRef}
            className="stage__camera-feed"
            data-testid="stage-camera-feed"
            data-mirrored="true"
            autoPlay
            playsInline
            muted
          />
        )}
        <div
          className="stage__canvas"
          ref={containerRef}
          data-testid="stage-canvas"
          data-scene-ready={sceneReady ? 'true' : 'false'}
          data-paused={paused ? 'true' : 'false'}
          data-preload-progress={preloadProgress.toFixed(2)}
          data-camera-stage={inputMode === 'KEYBOARD' ? 'false' : 'true'}
          data-pause-causes={pauseCauses.join(' ')}
          /*
           * What this sitting's scenes were handed (ACC-02).
           *
           * The same value that reaches `GamePresentationContext.reducedMotion` below, published
           * where a test standing outside the canvas can see it: a Phaser tween is invisible to a
           * browser, so without this the only end-to-end claim available would be "the checkbox was
           * ticked" — which says nothing about whether the setting travelled through setup, the
           * sitting and the stage to the scenes. What each effect then *does* with it is pinned in
           * both directions in the module's own tests.
           */
          data-reduced-motion={sessionLaunchConfig?.reducedMotion === true ? 'true' : 'false'}
        />

        {/*
         * "Come back where we can see you" — GDS §36's second rung.
         *
         * Over the stage rather than instead of it: §36's own last line is "never show a blank
         * screen", and a child who wandered off should come back to the mission they left, still
         * there, still waiting. It is drawn in the shell rather than by a scene because it is not
         * part of any game — every camera game gets the same one, and a paused Phaser scene cannot
         * animate its way out of a pause anyway.
         *
         * It contains no camera imagery of its own: the only picture of the child anywhere is the
         * feed that was already live behind the stage.
         */}
        {!showRecoveryOverlay ? null : (
          <div
            className="stage__recovery"
            data-testid="camera-recovery-overlay"
            data-loss-reason={recoveryReason ?? ''}
            role="status"
            aria-live="polite"
          >
            {/*
             * The theme's own picture for what has happened (Slice E″, brief §12).
             *
             * Decoration over the sentence, never instead of it: the words below are what a family
             * acts on, and they are unchanged. Same picture for every hero — a picture that marked
             * out the child the camera had lost would be a comparison.
             */}
            <StageArt assets={stageArt} assetId={stageRecoveryArtFor(recoveryReason)} />
            <h2>{recoveryInvitation.title}</h2>
            <p>{recoveryInvitation.body}</p>
            <p className="lede">{recoveryInvitation.reassurance}</p>
          </div>
        )}

        {/*
         * "Your spot is waiting" — one hero, while the mission carries on (Slice E′, D-076(2)(a)).
         *
         * Deliberately **not** an overlay: it sits in the corner of the stage rather than over it,
         * because a five-year-old whose sibling has wandered off must not have their mission covered
         * up. That is the ruling, made visible — one child's camera trouble is theirs alone and may
         * never block, slow or interrupt the other child by so much as a frame.
         *
         * It is addressed to the hero whose lane it is, by that hero's own name, and it says nothing
         * whatsoever about the other child: a sentence comparing the two of them — even "your
         * sibling is still playing" — is the comparison the non-competitive invariant forbids, and
         * D-076(6) extends that spirit to how well the camera can see a child.
         */}
        {laneInvitations.length === 0 ? null : (
          <div className="stage__lane-invitations">
            {laneInvitations.map((lane) => {
              const invitation = laneInvitationFor(lane.reason);
              return (
                <div
                  key={lane.playerId}
                  className="stage__lane-invite"
                  data-testid="camera-lane-invitation"
                  data-player-id={lane.playerId}
                  data-loss-reason={lane.reason ?? ''}
                  role="status"
                  aria-live="polite"
                >
                  <StageArt assets={stageArt} assetId={stageRecoveryArtFor(lane.reason)} />
                  <h3>{heroLabels[lane.playerId] ?? ''}</h3>
                  <p>{invitation.body}</p>
                  <p className="lede">{invitation.reassurance}</p>
                  {/*
                   * The per-child answer, said and then offered (Slice F′, retiring D-082(8)(a)).
                   *
                   * Slice E′ shipped this panel deliberately silent about the keyboard because the
                   * only election it had was sitting-wide, and offering that here would have taken
                   * the camera from a sibling who was playing happily (D-082(2)). The offer is per
                   * child now, so the true sentence can finally be said — and it is said to *this*
                   * hero about *their* keys, with nothing whatsoever about the other child.
                   *
                   * The sentence is unconditional; the button appears once this lane's own rung-4
                   * clock has run, which is the identical clock a solo child's offer runs on.
                   */}
                  <p className="lede">{INVENTED_RECOVERY_COPY.cooperative.laneKeysAlways}</p>
                  {!lane.offerKeys ? null : (
                    <button
                      type="button"
                      className="button button--quiet"
                      data-testid="camera-lane-keyboard-offer"
                      onClick={(event) => {
                        electLaneKeyboard(lane.playerId, event.currentTarget);
                      }}
                    >
                      {INVENTED_RECOVERY_COPY.cooperative.laneKeysAction}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/*
         * What a hero who took their keys reads afterwards (Slice F′).
         *
         * Addressed to them, promising only what is kept: this sitting has no way back to the
         * camera for one lane, so nothing here hints that there is one.
         */}
        {keyboardElectedHeroes.length === 0 || missionEnded ? null : (
          <div className="stage__lane-invitations">
            {keyboardElectedHeroes.map((playerId) => (
              <div
                key={playerId}
                className="stage__lane-invite"
                data-testid="camera-lane-keys-elected"
                data-player-id={playerId}
                role="status"
                aria-live="polite"
              >
                <h3>{heroLabels[playerId] ?? ''}</h3>
                <p>{INVENTED_RECOVERY_COPY.cooperative.laneKeysElected}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {stageError === null ? null : (
        <p className="notice" role="alert" data-testid="stage-error">
          The stage could not start: {stageError}
        </p>
      )}

      {/*
       * For the grown-up, when the machine is struggling (CAM-06).
       *
       * The diagnostic register of D-040(3)/D-064(5): it says what the game did about it, in words
       * a parent can act on, and it does not interrupt anybody. The child is told nothing, because
       * from where they are standing nothing has changed — their moves still count.
       */}
      {!showPerformanceNotice ? null : (
        <p className="notice" data-testid="camera-performance-notice">
          <strong>{INVENTED_RECOVERY_COPY.performance.title}</strong>{' '}
          {INVENTED_RECOVERY_COPY.performance.body}
        </p>
      )}

      {/*
       * GDS §36's fourth rung, offered rather than taken (Slice E).
       *
       * The keyboard has been live the whole time (CAM-09) — this is the moment a family is *told*
       * so, after the trouble has lasted long enough that waiting it out is no longer the kindest
       * option. It is a grown-up's decision: turning the camera off is exactly the sort of thing a
       * five-year-old should not be able to do by standing in the wrong place.
       */}
      {!showFallbackOffer ? null : (
        <div className="panel" data-testid="keyboard-fallback-offer">
          <h2 style={{ marginTop: 0 }}>{INVENTED_RECOVERY_COPY.fallback.title}</h2>
          <p className="lede">{INVENTED_RECOVERY_COPY.fallback.body}</p>
          <button
            type="button"
            className="button button--quiet"
            data-testid="keyboard-fallback-action"
            onClick={(event) => {
              electKeyboardFallback(event.currentTarget);
            }}
          >
            {INVENTED_RECOVERY_COPY.fallback.action}
          </button>
        </div>
      )}

      {/*
       * Architecture §20 refused this sitting's calibration, and says so (D-064(5)).
       *
       * A grown-up asked for the camera and did not get it; the honest thing is to say what would
       * change that. Diagnostic register, no interruption, and nothing child-facing — the child's
       * mission is running on the keys and looks exactly the same to them (CAM-09). The refusal
       * codes are exposed as data for the same reason the input feed is (D-040(3)).
       */}
      {cameraRefusals.length === 0 || missionEnded ? null : (
        <div
          className="notice"
          data-testid="camera-calibration-needed"
          data-refusals={cameraRefusals.join(' ')}
        >
          <strong>{INVENTED_CAMERA_STAGE_COPY.calibrationNeededTitle}</strong>{' '}
          {INVENTED_CAMERA_STAGE_COPY.calibrationNeededBody}
        </div>
      )}

      {/*
       * What the camera is reading a body as, right now. Grown-ups only, and never a comparison:
       * it names a room and a movement, not a child and not an ability.
       */}
      {playSpace === null || !cameraLive ? null : (
        <p className="notice" data-testid="camera-play-space">
          <strong>{INVENTED_CAMERA_STAGE_COPY.inEffect}:</strong>{' '}
          {CAMERA_MODE_COPY[playSpace.mode].label}
          {playSpace.substitute === null ? '' : ` — ${JUMP_SUBSTITUTE_COPY[playSpace.substitute]}`}
        </p>
      )}

      {!cameraFallbackElected || missionEnded ? null : (
        <p className="notice" data-testid="keyboard-fallback-elected">
          {INVENTED_RECOVERY_COPY.fallback.elected}
        </p>
      )}

      <div className="stage__readout" style={hiddenWhileSummary}>
        <div className="panel">
          <h2 style={{ marginTop: 0 }}>This round</h2>
          <p
            className="lede"
            data-testid="round-readout"
            data-content-id={round?.contentId ?? ''}
            data-stage-id={round?.stageId ?? ''}
            data-attempt={round === null ? '' : String(round.attemptNumber)}
            data-simplified={round?.simplified === true ? 'true' : 'false'}
            data-mission-ended={missionEnded ? 'true' : 'false'}
            data-mission-complete={missionComplete ? 'true' : 'false'}
            data-ended-early={endedEarly ? 'true' : 'false'}
            data-input-mode={inputMode}
            // Grown-up-facing diagnostics (D-040(3) family): what the body is being read as.
            data-camera-mode={playSpace?.mode ?? ''}
            data-jump-substitute={playSpace?.substitute ?? ''}
            /**
             * Which seats are answering with their bodies (Slice F′, D-076(5)).
             *
             * `data-input-mode` is a fact about the *sitting* and stays `MIXED`, which is the honest
             * word its own comment explains: the keys never stop working, so either input could have
             * produced any answer in the session being recorded. This is the per-seat truth that
             * word cannot carry, in the same grown-up diagnostic register as the two above.
             *
             * It names seats and wiring — never quality, never ability, never a comparison — and it
             * is an invisible attribute rather than anything a child reads (D-076(6) holds).
             */
            data-camera-heroes={cameraLaneHeroes.join(' ')}
            /**
             * The speakers, in the same grown-up diagnostic register as the camera attributes
             * above (Phase 5 Slice 0″). No new words on screen: these are attributes, not copy.
             *
             * `data-audio-playing` lists manifest IDs, never filenames, and there is deliberately
             * nothing per-child here to list — the one cue belongs to the team.
             */
            data-audio-unlocked={audioState?.unlocked === true ? 'true' : 'false'}
            data-audio-enabled={audioState?.enabled === true ? 'true' : 'false'}
            data-audio-ducked={audioState?.ducked === true ? 'true' : 'false'}
            data-audio-playing={(audioState?.playing ?? []).join(' ')}
            data-audio-heard={audioHeard.join(' ')}
          >
            {missionEnded
              ? endedEarly
                ? // A stopping place, not a failure and not a finish (game design spec §32).
                  'Good stopping place — your number shards are safe until next time.'
                : 'Mission complete — the number portals are working again.'
              : (round?.promptText ?? 'Getting the first question ready…')}
          </p>
          {round === null || round.choiceLabels.length === 0 ? null : (
            <ul className="event-feed" data-testid="round-choices">
              {round.choiceLabels.map((label, index) => (
                <li key={`${label}-${index}`}>{label}</li>
              ))}
            </ul>
          )}
          {assist === null ? null : (
            <p className="lede" data-testid="round-assist">
              {assist}
            </p>
          )}
          <p className="lede" data-testid="shard-count" data-shards={String(shards)}>
            Number shards: {shards}
          </p>
          <p
            className="lede"
            data-testid="narration-readout"
            data-utterances={String(narrationActivity.utterances)}
            /* Every line, in order, so a test can ask *which* line a keypress produced and how many
               times — a count and a latest line cannot tell a repeat from a hint (S5-8). */
            data-narration-history={JSON.stringify(narrationActivity.history)}
          >
            Bip-Bop said: {narrationActivity.lastText ?? 'nothing yet'}
          </p>
          {/* Reachable however the sitting ended: a summary a grown-up cannot get to is a
              dead end, and the sitting they stopped is the one they most want to read. */}
          {missionEnded && !summaryOnScreen ? (
            <button
              type="button"
              className="button button--quiet"
              data-testid="show-parent-summary"
              onClick={() => {
                setSummaryRequested(true);
              }}
            >
              Show parent summary
            </button>
          ) : null}
        </div>

        <div className="panel">
          <h2 style={{ marginTop: 0 }}>How to play</h2>
          <p className="lede" data-testid="key-help">
            <HeroKeyHelp playerId={HERO_1_PLAYER_ID} name={coopKeyHelp ? 'Hero 1' : null} />
            {coopKeyHelp ? <HeroKeyHelp playerId={HERO_2_PLAYER_ID} name="Hero 2" /> : null}
            Test keys: <kbd>F</kbd> freeze, <kbd>R</kbd> repeat the instruction, <kbd>P</kbd> pause.
          </p>
          {/*
           * The raw input feed, closed (R1-6).
           *
           * It names the hero behind every action, in the order the actions happened, and it sat
           * open on the child's own screen — directly contradicting the setup screen's promise
           * that the game never says which child answered. The promise is the thing worth keeping
           * true, so the feed is what moves: it stays in the DOM, because it is the diagnostic
           * seam the e2e suite reads (D-040(3)) and the one honest way for a grown-up to see what
           * the detector observed, but nobody has to look at it and no child sees it by default.
           */}
          <details className="diagnostics" data-testid="event-feed-details">
            <summary>Input diagnostics (for grown-ups)</summary>
            <ul className="event-feed" data-testid="event-feed">
              {feedLines.length === 0 ? (
                <li>No events yet.</li>
              ) : (
                feedLines.map((line, index) => <li key={`${line}-${index}`}>{line}</li>)
              )}
            </ul>
          </details>

          {/*
           * The CAM-08 counters (Slice H), in the same closed register as the feed above.
           *
           * CAM-08 is a measurement taken by a person watching a real child on real hardware —
           * "≥80% of deliberate actions recognised, fewer than three disruptive failures". What a
           * build can honestly contribute is the counting, so the person can watch the child
           * instead of keeping a tally. Closed by default, grown-up register, and about the
           * *camera*: a movement it could not read is a fact about a detector, never about a
           * five-year-old (D-059 PS3-1). Nothing here is stored, and only a camera sitting has it.
           */}
          {inputMode === 'KEYBOARD' ? null : (
            <details className="diagnostics" data-testid="camera-activity-details">
              <summary>{INVENTED_CAMERA_ACTIVITY_COPY.summary}</summary>
              <ul
                className="event-feed"
                data-testid="camera-activity"
                data-recognised-actions={String(cameraActivity.recognisedActions)}
                data-uncertainty-episodes={String(cameraActivity.uncertaintyEpisodes)}
                data-loss-episodes={String(cameraActivity.lossEpisodes)}
                data-fallback-offers={String(cameraActivity.fallbackOffers)}
                data-interruptions={String(disruptiveInterruptions(cameraActivity))}
                data-recognised-share={recognisedShare(cameraActivity)?.toFixed(2) ?? ''}
                /**
                 * What the one figure covers (Slice G′, D-076(6) + D-083(1)).
                 *
                 * Every figure above is the *sitting's*, and in a mixed sitting that is not the same
                 * as "both children": a hero on the keys contributes nothing to a camera count. The
                 * attribute names seats and wiring — never quality, never ability, never a child.
                 */
                data-camera-coverage={cameraCoverage}
              >
                <li>
                  {INVENTED_CAMERA_ACTIVITY_COPY.recognisedLabel}:{' '}
                  {cameraActivity.recognisedActions}
                </li>
                <li>
                  {INVENTED_CAMERA_ACTIVITY_COPY.uncertainLabel}:{' '}
                  {cameraActivity.uncertaintyEpisodes}
                </li>
                <li>
                  {INVENTED_CAMERA_ACTIVITY_COPY.interruptionsLabel}:{' '}
                  {disruptiveInterruptions(cameraActivity)}
                </li>
                <li>
                  {INVENTED_CAMERA_ACTIVITY_COPY.shareLabel}:{' '}
                  {recognisedShare(cameraActivity) === null
                    ? INVENTED_CAMERA_ACTIVITY_COPY.shareUnavailable
                    : `${Math.round((recognisedShare(cameraActivity) ?? 0) * 100)}%`}
                </li>
                <li className="lede">
                  {cameraCoverage === 'CAMERA_HEROES_ONLY'
                    ? INVENTED_CAMERA_ACTIVITY_COPY.coverageCameraHeroesOnly
                    : INVENTED_CAMERA_ACTIVITY_COPY.coverageEveryHero}
                </li>
                <li className="lede">{INVENTED_CAMERA_ACTIVITY_COPY.bound}</li>
              </ul>
            </details>
          )}
        </div>

        <div className="panel">
          <h2 style={{ marginTop: 0 }}>Assets resolved by ID</h2>
          <ul className="asset-report" data-testid="asset-report">
            {assetReports.map((report) => (
              <li
                key={report.requestedAssetId}
                data-asset-id={report.requestedAssetId}
                data-used-fallback={report.usedFallback ? 'true' : 'false'}
                data-failed={report.failed ? 'true' : 'false'}
              >
                {report.requestedAssetId}
                {report.failed
                  ? ' → could not load'
                  : report.usedFallback
                    ? ` → placeholder (${report.resolvedAssetId})`
                    : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
